import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, Printer, Trash2, Pencil, Check, X, GripVertical, LayoutGrid, Type as TypeIcon, Bold, Italic, AlignLeft, AlignCenter, AlignRight, Copy, Eye, EyeOff, Clock, Filter, Users, UserCheck } from 'lucide-react';
import { format, addDays, startOfWeek, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

import { roadmapApi, planningApi, moduleApi } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import { Planning, ABSENCE_TYPES, Template, Shift } from '../types';
import { isEmployeeOnSheetService, AbsenceTypeLike } from '../shiftServiceUtils';
import { generateRoadmapPDF } from '../../../lib/roadmapPdfGenerator';
import { AddRoadmapTableModal } from './AddRoadmapTableModal';
import ShiftCell from '../ShiftCell';
import ShiftEditor from '../ShiftEditor';

type Orientation = 'portrait' | 'landscape';

type TextAlign = 'left' | 'center' | 'right';

// Tableaux à colonnes personnalisables (demande d'Emeni) : chaque colonne a un type — 'text' (saisie
// manuelle, par rang) ou 'assign' (liste déroulante d'assignation) — au lieu de la paire fixe
// Rang/Salarié assigné d'origine.
interface RoadmapColumn { id: string; header: string; type: 'text' | 'assign'; position: number; }
interface RoadmapRow { id: string; position: number; fontSize: number; cells: Record<string, string>; }
interface RoadmapTable { id: string; title: string; position: number; posX: number; posY: number; width: number; minHeight: number; titleFontSize: number; columns: RoadmapColumn[]; rows: RoadmapRow[]; }
// Sous-feuille Midi ou Soir (ou l'unique sous-feuille d'une "feuille de route" en 1 feuille) — c'est
// elle qui porte réellement les tableaux, exactement comme avant cette évolution.
interface RoadmapSheet { id: string; service: 'midi' | 'soir'; position: number; tables: RoadmapTable[]; }
// "Feuille de route" nommée façon onglets Excel (demande d'Emeni : "un onglet avec un petit + pour
// ajouter une nouvelle feuille de route... on doit pouvoir les nommer", ex. une feuille "Salle" et une
// feuille "Cuisine"). Chacune garde EXACTEMENT la logique d'origine à l'intérieur — précision explicite
// d'Emeni après un premier essai trop simplifié : sheetMode 'split' = 2 sous-feuilles Midi/Soir avec
// leurs boutons Midi/Soir + "Copier vers..." habituels ; 'unified' = 1 seule sous-feuille de travail.
interface RoadmapBoard { id: string; name: string; sheetMode: 'split' | 'unified'; position: number; sheets: RoadmapSheet[]; }
interface Structure { boards: RoadmapBoard[]; }
interface TextBox { id: string; sheetId: string; content: string; posX: number; posY: number; width: number; height: number; fontSize: number; bold: boolean; italic: boolean; align: TextAlign; }
interface TitleLayout { label: string; posX: number; posY: number; width: number; fontSize: number; }
interface MetaLayout { posX: number; posY: number; fontSize: number; }
interface PageSettings { orientation: Orientation; marginTop: number; marginRight: number; marginBottom: number; marginLeft: number; }

const MIN_WIDTH = 220;
const MIN_TEXTBOX_HEIGHT = 60;
const MIN_TABLE_HEIGHT = 80;
// Référence stable (jamais recréée) pour le fallback "pas encore de feuille active" — un `[]` littéral
// inline recréerait un nouveau tableau à chaque rendu, ce qui suffit à faire boucler indéfiniment un
// useLayoutEffect qui en dépend.
const EMPTY_TABLES: RoadmapTable[] = [];
const PX_PER_MM = 3.7795275591;
const A4 = { portrait: { w: 210, h: 297 }, landscape: { w: 297, h: 210 } };
const SNAP_THRESHOLD = 6;
const GUIDE_COLOR = '#ff3d8f';

type DragKind = 'table' | 'textbox' | 'title' | 'meta';
interface Box { x: number; y: number; w: number; h: number; }
interface DragState { kind: DragKind; id: string; startX: number; startY: number; origX: number; origY: number; w: number; h: number; siblings: Box[]; curX: number; curY: number; }
interface ResizeState {
    kind: DragKind; id: string; startX: number; startY: number; origWidth: number; origHeight: number; curWidth: number; curHeight: number;
    // Tailles de police d'origine (rangs + titre du tableau), pour les faire suivre proportionnellement
    // au redimensionnement — demande d'Emeni ("la taille de la police s'adapte aussi").
    origRowFontSizes?: Record<string, number>; curRowFontSizes?: Record<string, number>;
    origTitleFontSize?: number; curTitleFontSize?: number;
    // Position propre (fixe pendant un redimensionnement) + boîtes des autres éléments, pour les
    // repères d'alignement de taille pendant le redimensionnement — demande d'Emeni : "avoir ces
    // indicateurs avec les lignes... pour savoir si [deux tableaux côte à côte] font bien la même
    // taille en haut et en bas".
    posX: number; posY: number; siblings: Box[];
}
const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 28;
interface Guide { type: 'v' | 'h'; pos: number; }
// Marge verticale d'un rang proportionnelle à sa taille de police (au lieu d'une marge fixe) — pour
// qu'un rang à petite police prenne vraiment moins de place, pas seulement un texte plus petit dans le
// même espace. Demande d'Emeni : "je devrais pouvoir resserrer les tableaux et les lignes se resserrent".
const rowPaddingPx = (fontSize: number) => Math.max(2, Math.round(fontSize * 0.3));

const FontStepper: React.FC<{ size: number; onChange: (delta: number) => void }> = ({ size, onChange }) => (
    <div className="flex items-center gap-0.5 shrink-0">
        <button onClick={() => onChange(-1)} className="px-1 text-slate-400 hover:text-slate-600 text-xs font-black">-</button>
        <span className="text-xs font-bold tabular-nums text-slate-500 dark:text-slate-400 w-5 text-center">{size}</span>
        <button onClick={() => onChange(1)} className="px-1 text-slate-400 hover:text-slate-600 text-xs font-black">+</button>
    </div>
);

type RangTier = 'never' | 'low' | 'ok';
const TIER_LABEL: Record<RangTier, string> = { never: "Jamais fait ce rang", low: "Pas assez fait ce rang", ok: "Habitué de ce rang" };

// Retire les accents pour que la recherche tape-à-la-volée trouve "regis" en tapant "regis" ou "régis".
const normalizeSearch = (s: string) => s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

// Liste personnalisée (pas un <select> natif) pour pouvoir afficher prénom + poste + un code couleur
// de roulement par option, demandé par Emeni — un <select> HTML ne permet pas ce niveau de rendu.
// Plusieurs salariés peuvent être cochés sur le même rang (autre demande d'Emeni) : la liste ne se
// referme donc plus au premier clic, seulement en cliquant en dehors ou sur "Fermer". Un champ de
// recherche en haut permet de taper les premières lettres du prénom (demande d'Emeni : "c'est plus
// facile") — Entrée assigne directement le premier résultat filtré.
const AssignDropdown: React.FC<{
    rowId: string;
    assignedIds: string[];
    sortedList: any[];
    notEligibleAssignedIds: string[];
    employeeNameById: Record<string, string>;
    employeeFirstNameById: Record<string, string>;
    employeePosteById: Record<string, string>;
    rangTier: (rowId: string, empId: string) => RangTier;
    tierColor: Record<RangTier, string>;
    isOpen: boolean;
    fontSize: number;
    onToggle: () => void;
    onClose: () => void;
    onToggleAssign: (employeeId: string) => void;
    onClear: () => void;
}> = ({ rowId, assignedIds, sortedList, notEligibleAssignedIds, employeeFirstNameById, employeePosteById, rangTier, tierColor, isOpen, fontSize, onToggle, onClose, onToggleAssign, onClear }) => {
    const [search, setSearch] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    // Position calculée en viewport (pas relative au rang) car le panneau est rendu via un portail —
    // sans ça, il restait piégé dans le conteneur défilant/empilable du tableau et pouvait se retrouver
    // masqué sous d'autres éléments de la page (ex. le panneau "Aperçu planning" à droite), remonté par
    // Emeni : "la liste des noms passe en dessous du planning, on n'a pas accès à la barre déroulante".
    const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number } | null>(null);

    useEffect(() => {
        if (isOpen) {
            setSearch('');
            const rect = buttonRef.current?.getBoundingClientRect();
            if (rect) {
                const width = 320;
                const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
                setPanelPos({ top: rect.bottom + 4, left, width });
            }
            const t = setTimeout(() => searchInputRef.current?.focus(), 0);
            // Le panneau est positionné en coordonnées écran au moment de l'ouverture ; s'il y a un
            // défilement pendant qu'il est ouvert, on le referme plutôt que de le laisser se décoller
            // visuellement du rang (plus simple et plus sûr que recalculer sa position en continu).
            const onScroll = () => onClose();
            window.addEventListener('scroll', onScroll, true);
            return () => { clearTimeout(t); window.removeEventListener('scroll', onScroll, true); };
        }
    }, [isOpen]);

    const query = normalizeSearch(search.trim());
    const filteredSortedList = query ? sortedList.filter(emp => normalizeSearch(employeeFirstNameById[emp.id] || '').includes(query)) : sortedList;
    const filteredNotEligible = query ? notEligibleAssignedIds.filter(id => normalizeSearch(employeeFirstNameById[id] || '').includes(query)) : notEligibleAssignedIds;

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') { onClose(); return; }
        if (e.key === 'Enter') {
            const first = filteredSortedList[0] || (filteredNotEligible[0] ? { id: filteredNotEligible[0] } : null);
            if (first) { onToggleAssign(first.id); setSearch(''); }
        }
    };

    return (
    <div className="relative flex-1 min-w-0">
        <button
            ref={buttonRef}
            type="button"
            onClick={onToggle}
            style={{ fontSize, paddingTop: rowPaddingPx(fontSize), paddingBottom: rowPaddingPx(fontSize) }}
            className="w-full text-left bg-slate-50 dark:bg-[var(--bg-soft)] border border-slate-200 dark:border-border rounded-lg px-2 outline-none focus:border-brand-500 break-words"
        >
            {assignedIds.map(id => employeeFirstNameById[id] || '').filter(Boolean).join(', ')}
        </button>
        {isOpen && panelPos && createPortal(
            <>
                <div className="fixed inset-0 z-[100]" onClick={onClose} />
                <div
                    className="fixed max-h-80 overflow-y-auto bg-white dark:bg-[var(--bg-card)] border border-slate-200 dark:border-border rounded-xl shadow-xl z-[101] py-1"
                    style={{ top: panelPos.top, left: panelPos.left, width: panelPos.width }}
                >
                    <div className="px-2 pt-1 pb-2 sticky top-0 bg-white dark:bg-[var(--bg-card)] z-10">
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Taper un prénom..."
                            className="w-full text-sm bg-slate-50 dark:bg-[var(--bg-soft)] border border-slate-200 dark:border-border rounded-lg px-2 py-1.5 outline-none focus:border-brand-500"
                        />
                    </div>
                    <button onClick={() => { onClear(); onClose(); }} className="w-full text-left px-3 py-2 text-sm text-slate-400 hover:bg-slate-50 dark:hover:bg-surface-soft border-b border-slate-100 dark:border-border">Non assigné (tout retirer)</button>
                    {filteredNotEligible.map(id => (
                        <button key={id} onClick={() => onToggleAssign(id)} className="w-full flex items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-surface-soft bg-brand-50/60 dark:bg-brand-500/10">
                            <Check size={14} className="shrink-0 mt-0.5 text-brand-500" />
                            <span className="min-w-0">
                                <span className="block text-sm font-medium text-slate-700 dark:text-content break-words">{employeeFirstNameById[id] || 'Salarié'}</span>
                                <span className="block text-xs text-slate-400">non prévu aujourd'hui</span>
                            </span>
                        </button>
                    ))}
                    {filteredSortedList.map(emp => {
                        const tier = rangTier(rowId, emp.id);
                        const poste = employeePosteById[emp.id];
                        const checked = assignedIds.includes(emp.id);
                        return (
                            <button
                                key={emp.id}
                                onClick={() => onToggleAssign(emp.id)}
                                className={`w-full flex items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-surface-soft ${checked ? 'bg-brand-50 dark:bg-brand-500/10' : ''}`}
                                title={TIER_LABEL[tier]}
                            >
                                {checked ? <Check size={14} className="shrink-0 mt-0.5 text-brand-500" /> : <span className="w-2 h-2 rounded-full shrink-0 mt-1.5" style={{ backgroundColor: tierColor[tier] }} />}
                                <span className="min-w-0">
                                    <span className="block text-sm font-medium text-slate-700 dark:text-content break-words">{employeeFirstNameById[emp.id]}</span>
                                    {poste && <span className="block text-xs text-slate-400 break-words">{poste}</span>}
                                </span>
                            </button>
                        );
                    })}
                    {filteredSortedList.length === 0 && filteredNotEligible.length === 0 && (
                        <p className="px-3 py-2 text-xs text-slate-400">{query ? 'Aucun salarié ne correspond.' : 'Aucun salarié prévu ce jour sur ce service.'}</p>
                    )}
                    <button onClick={onClose} className="w-full text-center px-3 py-2 text-xs font-bold text-brand-500 border-t border-slate-100 dark:border-border hover:bg-slate-50 dark:hover:bg-surface-soft">Fermer</button>
                </div>
            </>,
            document.body
        )}
    </div>
    );
};

export const RoadmapPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Si on arrive depuis Planning sur une semaine précise (bouton "Feuille de route" pendant qu'une
    // autre semaine que la semaine en cours est affichée), on ouvre directement sur cette semaine-là
    // (son lundi) au lieu de toujours retomber sur aujourd'hui.
    const initialDate = (() => {
        const param = searchParams.get('date');
        if (param) { const d = parseISO(param); if (!isNaN(d.getTime())) return d; }
        return new Date();
    })();

    const [date, setDate] = useState<Date>(initialDate);
    // activeBoardId = onglet "feuille de route" actif (le "+" façon Excel) ; activeService = sous-onglet
    // Midi/Soir à l'intérieur, uniquement pertinent quand cette feuille de route est en "2 feuilles".
    const [activeBoardId, setActiveBoardId] = useState<string | null>(null);
    const [activeService, setActiveService] = useState<'midi' | 'soir'>('midi');
    const [structure, setStructure] = useState<Structure>({ boards: [] });
    const [textBoxes, setTextBoxes] = useState<TextBox[]>([]);
    // Renommage/création de feuille de route (onglets façon Excel) — demande d'Emeni.
    const [editingBoardId, setEditingBoardId] = useState<string | null>(null);
    const [boardNameDraft, setBoardNameDraft] = useState('');
    const [addingBoard, setAddingBoard] = useState(false);
    const [newBoardNameDraft, setNewBoardNameDraft] = useState('');
    const [newBoardSheetMode, setNewBoardSheetMode] = useState<'split' | 'unified'>('split');
    // rowId -> columnId -> employeeIds affectés (une colonne "assign" peut désormais exister plusieurs
    // fois par tableau — demande d'Emeni).
    const [assignments, setAssignments] = useState<Record<string, Record<string, string[]>>>({});
    const [modifiedToday, setModifiedToday] = useState(true);
    const [lastModifiedDate, setLastModifiedDate] = useState<string | null>(null);
    const [rotation, setRotation] = useState<{ byRow: Record<string, Record<string, number>>; byEmployee: Record<string, string[]> }>({ byRow: {}, byEmployee: {} });
    const [employees, setEmployees] = useState<any[]>([]);
    const [absenceTypes, setAbsenceTypes] = useState<{ code: string; isFullDay: boolean; autoApply: boolean; color?: string }[]>(
        ABSENCE_TYPES.map(c => ({ code: c, isFullDay: true, autoApply: true, color: (String(c) === 'REPOS' ? '#000000' : '#ffe39b') }))
    );
    const [jobPosts, setJobPosts] = useState<{ id: string; title: string }[]>([]);
    const [planningWeek, setPlanningWeek] = useState<Planning | null>(null);
    // Chargés pour que l'aperçu planning (panneau optionnel) rende les créneaux exactement comme le
    // vrai module Planning (mêmes couleurs de gabarit) via le composant ShiftCell partagé.
    const [templates, setTemplates] = useState<Template[]>([]);
    const [rolesOrder, setRolesOrder] = useState<string[]>([]);
    const [titleLayout, setTitleLayout] = useState<TitleLayout>({ label: 'Feuille de route', posX: 20, posY: 20, width: 300, fontSize: 22 });
    const [metaLayout, setMetaLayout] = useState<MetaLayout>({ posX: 20, posY: 55, fontSize: 11 });
    const [pageSettings, setPageSettings] = useState<PageSettings>({ orientation: 'portrait', marginTop: 15, marginRight: 15, marginBottom: 15, marginLeft: 15 });
    // Panneau optionnel affichant le planning du jour (salariés + horaires) dans l'espace vide à droite
    // de la feuille — demande d'Emeni, écran uniquement (jamais imprimé), activable/désactivable.
    const [showDayPreview, setShowDayPreview] = useState(false);
    // La liste d'assignation d'un rang ne montre par défaut que les salariés prévus au planning ce jour-là
    // (eligibleEmployees) — certains clients veulent pouvoir choisir n'importe quel salarié, y compris ceux
    // non prévus ce jour-là, demande d'Emeni : "un bouton activable/désactivable", pas un comportement fixe.
    const [assignShowAllEmployees, setAssignShowAllEmployees] = useState(false);
    // Filtre par poste de l'aperçu planning (demande d'Emeni) — liste vide = tous les postes affichés.
    const [dayPreviewRoleFilter, setDayPreviewRoleFilter] = useState<string[]>([]);
    const [dayPreviewFilterOpen, setDayPreviewFilterOpen] = useState(false);
    // L'aperçu montre par défaut tout le monde (travail + repos + absences, demande d'Emeni) — ce filtre
    // additionnel permet de ne garder que ceux effectivement sur le service (en travail ce jour-là).
    const [dayPreviewOnServiceOnly, setDayPreviewOnServiceOnly] = useState(false);
    // Rendu via portail (voir AssignDropdown) — sans ça, le menu déroulant est piégé dans le conteneur
    // défilant du panneau "Aperçu planning" (overflow-y-auto) et se retrouve coupé/masqué derrière sa
    // propre barre de défilement, remonté par Emeni avec capture à l'appui ("le filtre se retrouve couper").
    const dayPreviewFilterBtnRef = useRef<HTMLButtonElement>(null);
    const [dayPreviewFilterPos, setDayPreviewFilterPos] = useState<{ top: number; left: number } | null>(null);
    // Éditer un créneau directement depuis l'aperçu planning (demande d'Emeni : "il doit être modifiable
    // comme le planning de base, et se mettre à jour logiquement sur le planning") — même éditeur
    // (ShiftEditor) et même route de sauvegarde (planningApi.saveWeek) que le vrai module Planning : ce
    // n'est pas une copie des données, donc toute modification ici se reflète immédiatement là-bas.
    const [editingDayPreviewShift, setEditingDayPreviewShift] = useState<{ shift: Shift; employeeId: string; employeeName: string; employeeRole: string; date: string } | null>(null);
    const [dayPreviewEditorPos, setDayPreviewEditorPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    // Couleurs du code couleur de roulement (jamais fait / pas assez / habitué), personnalisables par
    // établissement — demande d'Emeni.
    const [tierColors, setTierColors] = useState<Record<RangTier, string>>({ never: '#ef4444', low: '#f59e0b', ok: '#22c55e' });
    const [loading, setLoading] = useState(true);

    const [addTableOpen, setAddTableOpen] = useState(false);
    const [pageMenuOpen, setPageMenuOpen] = useState(false);
    const [editingTitleLabel, setEditingTitleLabel] = useState(false);
    const [titleLabelDraft, setTitleLabelDraft] = useState('Feuille de route');
    const [editingTableId, setEditingTableId] = useState<string | null>(null);
    const [tableTitleDraft, setTableTitleDraft] = useState('');
    const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
    const [headerDraft, setHeaderDraft] = useState('');
    const [addRowForTable, setAddRowForTable] = useState<string | null>(null);
    const [rowLabelDraft, setRowLabelDraft] = useState('');
    // Cellule de texte libre en cours d'édition (tableaux à colonnes personnalisables) — remplace
    // l'ancien "un seul champ label par rang", chaque colonne de type texte est éditable séparément.
    const [editingCell, setEditingCell] = useState<{ rowId: string; columnId: string } | null>(null);
    const [editCellDraft, setEditCellDraft] = useState('');
    // Élément "sélectionné" (dernier tableau/zone de texte cliqué) pour le raccourci Ctrl+C/Ctrl+V —
    // demande d'Emeni, en plus d'un bouton "Dupliquer" explicite sur chaque tableau/zone.
    const [selectedElement, setSelectedElement] = useState<{ kind: 'table' | 'textbox'; id: string } | null>(null);
    // Presse-papiers Ctrl+C/Ctrl+V distinct de la sélection (demande d'Emeni : "copier un tableau d'une
    // feuille pour le coller sur une autre feuille de route") — contrairement à selectedElement (qui suit
    // le dernier clic), le presse-papiers persiste quand on change d'onglet/feuille de route entre le
    // copier et le coller.
    const [clipboard, setClipboard] = useState<{ kind: 'table' | 'textbox'; id: string } | null>(null);
    const [editingTextBoxId, setEditingTextBoxId] = useState<string | null>(null);
    // Clé `${rowId}:${columnId}` de la liste d'assignation ouverte — plusieurs colonnes "assign"
    // peuvent désormais exister sur un même rang.
    const [openAssignKey, setOpenAssignKey] = useState<string | null>(null);
    const [copyingService, setCopyingService] = useState(false);

    const dragRef = useRef<DragState | null>(null);
    const resizeRef = useRef<ResizeState | null>(null);
    const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const setNodeRef = (key: string) => (el: HTMLDivElement | null) => { nodeRefs.current[key] = el; };
    const [guides, setGuides] = useState<Guide[]>([]);

    // Repères visuels reliant les rangs assignés au même salarié (demande d'Emeni : "quand un salarié
    // est assigné à plusieurs lignes on doit avoir une flèche qui relie ces lignes, sans les déplacer").
    // Refs séparées de nodeRefs (qui sert aussi à la collecte de "siblings" pour le glisser des
    // tableaux/zones de texte — y mélanger les rangs y introduirait des faux positifs).
    const rowBodyRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const setRowBodyRef = (tableId: string) => (el: HTMLDivElement | null) => { rowBodyRefs.current[tableId] = el; };
    const rowNodeRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const setRowNodeRef = (rowId: string) => (el: HTMLDivElement | null) => { rowNodeRefs.current[rowId] = el; };
    const [connectorsByTable, setConnectorsByTable] = useState<Record<string, { top: number; bottom: number; dots: number[]; color: string; lane: number }[]>>({});
    const [connectorLanesByTable, setConnectorLanesByTable] = useState<Record<string, number>>({});
    const CONNECTOR_LANE_WIDTH = 11;
    const CONNECTOR_GUTTER_PAD = 12;
    const CONNECTOR_COLORS = ['#f97316', '#8b5cf6', '#0ea5e9', '#ec4899', '#14b8a6', '#eab308', '#6366f1', '#ef4444'];
    const colorForEmployee = (empId: string) => {
        let hash = 0;
        for (let i = 0; i < empId.length; i++) hash = (hash * 31 + empId.charCodeAt(i)) | 0;
        return CONNECTOR_COLORS[Math.abs(hash) % CONNECTOR_COLORS.length];
    };

    const dateStr = format(date, 'yyyy-MM-dd');
    const mondayStr = format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');

    const activeBoard = useMemo(() => structure.boards.find(b => b.id === activeBoardId) || null, [structure, activeBoardId]);
    // La sous-feuille effectivement affichée : en "1 feuille" c'est l'unique sous-feuille de la feuille
    // de route ; en "2 feuilles" c'est Midi ou Soir selon l'onglet secondaire choisi.
    const activeSheet = useMemo(() => {
        if (!activeBoard) return null;
        if (activeBoard.sheetMode === 'unified') return activeBoard.sheets[0] || null;
        return activeBoard.sheets.find(s => s.service === activeService) || null;
    }, [activeBoard, activeService]);
    const activeSheetId = activeSheet?.id ?? null;

    // Petit utilitaire pour mettre à jour uniquement les tableaux de la sous-feuille indiquée (par
    // défaut la sous-feuille active) sans toucher aux autres.
    const updateSheetTables = (sheetId: string | null, updater: (tables: RoadmapTable[]) => RoadmapTable[]) => {
        if (!sheetId) return;
        setStructure(prev => ({
            boards: prev.boards.map(b => ({ ...b, sheets: b.sheets.map(s => s.id === sheetId ? { ...s, tables: updater(s.tables) } : s) })),
        }));
    };

    // --- Chargement initial (structure, zones de texte, employés, postes, réglages) ---
    const loadInitial = useCallback(async () => {
        setLoading(true);
        try {
            const [boxes, emps, posts, settings, planningSettings, tpls] = await Promise.all([
                roadmapApi.getTextBoxes(),
                moduleApi.getEmployes(),
                moduleApi.getJobPosts(),
                roadmapApi.getSettings(),
                planningApi.getSettings(),
                planningApi.getTemplates(),
            ]);
            setTextBoxes(Array.isArray(boxes) ? boxes : []);
            setEmployees(Array.isArray(emps) ? emps : []);
            setJobPosts(Array.isArray(posts) ? posts.map((p: any) => ({ id: p.id, title: p.title })) : []);
            setTemplates(Array.isArray(tpls) ? tpls : []);

            const label = settings?.titleLabel || 'Feuille de route';
            setTitleLayout({
                label,
                posX: settings?.titlePosX ?? 20,
                posY: settings?.titlePosY ?? 20,
                width: settings?.titleWidth ?? 300,
                fontSize: settings?.titleFontSize ?? 22,
            });
            setTitleLabelDraft(label);
            setPageSettings({
                orientation: settings?.orientation === 'landscape' ? 'landscape' : 'portrait',
                marginTop: settings?.marginTop ?? 15,
                marginRight: settings?.marginRight ?? 15,
                marginBottom: settings?.marginBottom ?? 15,
                marginLeft: settings?.marginLeft ?? 15,
            });
            setMetaLayout({
                posX: settings?.metaPosX ?? 20,
                posY: settings?.metaPosY ?? 55,
                fontSize: settings?.metaFontSize ?? 11,
            });
            setShowDayPreview(!!settings?.showDayPreview);
            setAssignShowAllEmployees(!!settings?.assignShowAllEmployees);
            setTierColors({
                never: settings?.tierColorNever || '#ef4444',
                low: settings?.tierColorLow || '#f59e0b',
                ok: settings?.tierColorOk || '#22c55e',
            });

            // Même mapping que PlanningModule.tsx (isFullDay/autoApply/color) — nécessaire pour rendre
            // l'aperçu planning éditable via le même ShiftEditor, qui a besoin de ces champs pour les
            // absences (pas seulement le code), pas seulement pour l'affichage ShiftCell.
            const configuredAbsenceTypes = Array.isArray(planningSettings?.absenceCodes) && planningSettings.absenceCodes.length
                ? planningSettings.absenceCodes.map((v: any) => {
                    const codeStr = v?.code || v?.label || (typeof v === 'string' ? v : 'ABS');
                    return {
                        code: String(codeStr),
                        isFullDay: v?.isFullDay ?? true,
                        autoApply: v?.autoApply ?? true,
                        color: v?.color || (String(codeStr) === 'REPOS' ? '#000000' : '#ffe39b'),
                    };
                })
                : ABSENCE_TYPES.map(c => ({ code: c, isFullDay: true, autoApply: true, color: (String(c) === 'REPOS' ? '#000000' : '#ffe39b') }));
            setAbsenceTypes(configuredAbsenceTypes);

            // Même ordre de postes que le module Planning (Réglages > Ordre des postes) — demande
            // d'Emeni pour que l'aperçu planning respecte cet ordre au lieu d'un tri alphabétique.
            const configuredRolesOrder = Array.isArray((planningSettings as any)?.rolesOrder)
                ? (planningSettings as any).rolesOrder.map((v: any) => String(v))
                : [];
            setRolesOrder(configuredRolesOrder);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadInitial(); }, [loadInitial]);

    // --- Rechargement par date (affectations + planning de la semaine) ---
    const refreshAssignments = useCallback(async () => {
        const [asg, week] = await Promise.all([
            roadmapApi.getAssignments(dateStr),
            planningApi.getWeek(mondayStr),
        ]);
        setAssignments(asg?.assignments || {});
        setModifiedToday(asg?.modifiedToday ?? true);
        setLastModifiedDate(asg?.lastModifiedDate ?? null);
        setPlanningWeek(week || null);
    }, [dateStr, mondayStr]);

    useEffect(() => { refreshAssignments(); }, [refreshAssignments]);

    // --- Rechargement par sous-feuille (roulement) ---
    useEffect(() => {
        if (!activeSheetId) return;
        (async () => {
            const rot = await roadmapApi.getRotation(activeSheetId);
            setRotation(rot || { byRow: {}, byEmployee: {} });
        })();
    }, [activeSheetId]);

    // Rechargée à chaque changement de date : un tableau/rang/feuille de route supprimé disparaît
    // seulement à partir de sa date de suppression, il faut donc redemander la structure pour la date
    // affichée. Garde l'onglet actif s'il existe toujours pour cette date, sinon retombe sur le premier.
    const refreshStructure = useCallback(async () => {
        const struct = await roadmapApi.getStructure(dateStr);
        const boards: RoadmapBoard[] = struct?.boards || [];
        setStructure({ boards });
        setActiveBoardId(prev => (prev && boards.some(b => b.id === prev)) ? prev : (boards[0]?.id ?? null));
    }, [dateStr]);

    useEffect(() => { refreshStructure(); }, [refreshStructure]);

    const refreshTextBoxes = useCallback(async () => {
        const boxes = await roadmapApi.getTextBoxes();
        setTextBoxes(Array.isArray(boxes) ? boxes : []);
    }, []);

    const refreshRotation = useCallback(async () => {
        if (!activeSheetId) return;
        const rot = await roadmapApi.getRotation(activeSheetId);
        setRotation(rot || { byRow: {}, byEmployee: {} });
    }, [activeSheetId]);

    const employeeNameById = useMemo(() => {
        const map: Record<string, string> = {};
        employees.forEach(e => { map[e.id] = `${(e.last_name || '').toUpperCase()} ${e.first_name || ''}`.trim(); });
        return map;
    }, [employees]);

    const employeeFirstNameById = useMemo(() => {
        const map: Record<string, string> = {};
        employees.forEach(e => { map[e.id] = e.first_name || employeeNameById[e.id] || ''; });
        return map;
    }, [employees, employeeNameById]);

    const employeePosteById = useMemo(() => {
        const map: Record<string, string> = {};
        employees.forEach(e => { map[e.id] = e.job_post_title || e.position || ''; });
        return map;
    }, [employees]);

    const eligibleEmployees = useMemo(() => {
        // "Tous les salariés" (demande d'Emeni) : plus aucun filtre par service/jour, toute la liste des
        // salariés est assignable — utile si le client veut pouvoir piocher en dehors de ce qui est prévu.
        if (assignShowAllEmployees) return employees;
        // En "1 feuille", l'éligibilité s'étend aux deux services (midi + soir confondus) — comportement
        // historique inchangé, désormais par feuille de route plutôt que global.
        const svc: 'midi' | 'soir' | 'both' = activeBoard?.sheetMode === 'unified' ? 'both' : (activeSheet?.service ?? 'midi');
        return employees.filter(emp => {
            const row = planningWeek?.rows?.find(r => r.employeeId === emp.id);
            const shift = row?.shifts?.[dateStr];
            return isEmployeeOnSheetService(shift, svc, absenceTypes);
        });
    }, [employees, planningWeek, dateStr, activeBoard, activeSheet, absenceTypes, assignShowAllEmployees]);

    // Aperçu du planning du jour (salariés + créneaux, vue "jour") pour le panneau optionnel à droite
    // de la feuille — aide mémoire pendant qu'on assigne, sans redemander à ouvrir le module Planning.
    // Garde le Shift complet (pas juste les heures en texte) pour être rendu par le même composant
    // ShiftCell que le vrai Planning, avec les mêmes couleurs de gabarit — demande explicite d'Emeni
    // ("je veux qu'il ressemble au planning réel déjà existant").
    const getMinutes = (time: string | undefined) => {
        if (!time || typeof time !== 'string') return 0;
        const [h, m] = time.split(':').map(Number);
        return (h || 0) * 60 + (m || 0);
    };
    const calculateShiftDuration = (shift: Shift | undefined) => {
        if (!shift?.segments) return 0;
        return shift.segments.reduce((acc, seg) => {
            if (seg.type === 'horaire' && seg.start && seg.end) {
                let start = getMinutes(seg.start);
                let end = getMinutes(seg.end);
                if (end < start) end += 24 * 60;
                return acc + (end - start);
            }
            return acc;
        }, 0);
    };
    const formatDuration = (minutes: number) => {
        if (minutes <= 0) return null;
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${h}h${m > 0 ? m.toString().padStart(2, '0') : ''}`;
    };

    // Même ordre de postes que le module Planning (rolesOrder configuré en premier, puis le reste des
    // postes dans leur ordre de création) plutôt qu'un tri alphabétique — demande d'Emeni.
    const roleSortIndex = useMemo(() => {
        const index: Record<string, number> = {};
        let i = 0;
        rolesOrder.forEach(id => { if (!(id in index)) index[id] = i++; });
        jobPosts.forEach(p => { if (!(String(p.id) in index)) index[String(p.id)] = i++; });
        return index;
    }, [rolesOrder, jobPosts]);

    const dayPreviewList = useMemo(() => {
        if (!planningWeek?.rows) return [];
        return planningWeek.rows
            .map(row => {
                const shift = row.shifts?.[dateStr];
                // Tous les salariés du jour, pas seulement ceux en travail — demande d'Emeni : "je dois voir
                // la liste de tous les salariés sur la journée, même ceux avec un code absence, repos etc."
                // Un salarié sans aucun Shift renseigné ce jour-là (jamais saisi) reste exclu : rien à afficher.
                if (!shift || !shift.segments || shift.segments.length === 0) return null;
                const horaires = (shift.segments || []).filter(seg => seg.type === 'horaire' && seg.start && seg.end);
                // employeeRole est un id de poste (comme dans PlanningModule.tsx/getRoleLabel), pas un
                // libellé — sans cette résolution, l'aperçu affichait l'id brut à la place du poste.
                const roleId = String(row.employeeRole);
                const roleLabel = jobPosts.find(p => String(p.id) === roleId)?.title || 'Sans poste';
                return {
                    employeeId: row.employeeId,
                    name: employeeFirstNameById[row.employeeId] || row.employeeName,
                    role: roleLabel,
                    roleId,
                    roleSortIndex: roleSortIndex[roleId] ?? Number.MAX_SAFE_INTEGER,
                    isExtra: row.isExtra,
                    shift,
                    durationLabel: formatDuration(calculateShiftDuration(shift)),
                    // Triés par horaire de début ; repos/absence (pas d'horaire) relégués après les
                    // horaires du jour, puis par nom.
                    firstStart: horaires[0]?.start || '~',
                };
            })
            .filter((r): r is NonNullable<typeof r> => !!r)
            .sort((a, b) => a.roleSortIndex - b.roleSortIndex || a.firstStart.localeCompare(b.firstStart) || a.name.localeCompare(b.name));
    }, [planningWeek, dateStr, employeeFirstNameById, jobPosts, roleSortIndex]);

    // Regroupé par poste (demande d'Emeni : "bien trier par poste"), comme les groupes de rôle du vrai
    // module Planning. Filtré par poste (demande d'Emeni : "un filtre avec les postes qu'on coche pour
    // afficher que ceux qu'on a coché") — liste vide = aucun filtre, tous les postes affichés.
    const dayPreviewGroups = useMemo(() => {
        let list = dayPreviewRoleFilter.length > 0 ? dayPreviewList.filter(p => dayPreviewRoleFilter.includes(p.roleId)) : dayPreviewList;
        if (dayPreviewOnServiceOnly) list = list.filter(p => p.shift.type === 'travail');
        const groups: { role: string; people: typeof dayPreviewList }[] = [];
        for (const p of list) {
            const last = groups[groups.length - 1];
            if (last && last.role === p.role) last.people.push(p);
            else groups.push({ role: p.role, people: [p] });
        }
        return groups;
    }, [dayPreviewList, dayPreviewRoleFilter, dayPreviewOnServiceOnly]);

    useEffect(() => {
        if (!dayPreviewFilterOpen) return;
        const rect = dayPreviewFilterBtnRef.current?.getBoundingClientRect();
        if (rect) {
            const width = 224;
            const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
            setDayPreviewFilterPos({ top: rect.bottom + 4, left });
        }
        const onScroll = () => setDayPreviewFilterOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [dayPreviewFilterOpen]);

    // Extras (renforts ponctuels, ex. "2x Hôtesse LBE") du jour — distincts des salariés, ils vivent
    // dans planningWeek.extraShifts et n'apparaissaient pas dans l'aperçu jusqu'ici. Demande d'Emeni :
    // "dans l'aperçu du planning on doit voir s'il y a des extras".
    const dayExtras = useMemo(() => (planningWeek?.extraShifts || []).filter(e => e.date === dateStr), [planningWeek, dateStr]);

    // Code couleur du roulement par (rang, colonne) : 0 fois = jamais fait, 1-2 fois = pas assez,
    // 3+ = habitué. Par colonne car un tableau peut avoir plusieurs colonnes d'assignation.
    const RANG_LOW_THRESHOLD = 3;
    const rangCount = useCallback((rowId: string, columnId: string, empId: string) => rotation.byRow[`${rowId}:${columnId}`]?.[empId] || 0, [rotation]);
    const rangTier = useCallback((rowId: string, columnId: string, empId: string): 'never' | 'low' | 'ok' => {
        const n = rangCount(rowId, columnId, empId);
        return n === 0 ? 'never' : n < RANG_LOW_THRESHOLD ? 'low' : 'ok';
    }, [rangCount]);

    const sortedEligibleFor = useCallback((rowId: string, columnId: string) => {
        const coverage = (empId: string) => (rotation.byEmployee[empId] || []).length;
        return [...eligibleEmployees].sort((a, b) => {
            const ac = rangCount(rowId, columnId, a.id), bc = rangCount(rowId, columnId, b.id);
            if (ac !== bc) return ac - bc;
            const cc = coverage(a.id), dc = coverage(b.id);
            if (cc !== dc) return cc - dc;
            return (employeeNameById[a.id] || '').localeCompare(employeeNameById[b.id] || '');
        });
    }, [eligibleEmployees, rotation, employeeNameById, rangCount]);

    // --- Actions tableaux / rangs ---
    const handleTableCreated = async () => { setAddTableOpen(false); await refreshStructure(); };

    const saveTableTitle = async (id: string) => {
        if (tableTitleDraft.trim()) await roadmapApi.renameTable(id, tableTitleDraft.trim());
        setEditingTableId(null);
        await refreshStructure();
    };

    const deleteTable = async (id: string) => {
        if (!window.confirm("Supprimer ce tableau à partir d'aujourd'hui ? Il restera visible sur les jours déjà modifiés avant aujourd'hui.")) return;
        await roadmapApi.deleteTable(id);
        await refreshStructure();
        await refreshRotation();
    };

    // Dupliquer un tableau (bouton "Dupliquer", toujours à côté de l'original) ou le coller sur une
    // AUTRE feuille de route via Ctrl+C/Ctrl+V (demande d'Emeni) — `targetSheetId` copie la mise en
    // page, les rangs et les affectations du jour vers cette sous-feuille au lieu de la sienne.
    const duplicateTable = async (id: string, targetSheetId?: string) => {
        await roadmapApi.duplicateTable(id, dateStr, targetSheetId);
        await Promise.all([refreshStructure(), refreshRotation()]);
    };

    const submitNewRow = async (tableId: string) => {
        await roadmapApi.addRow(tableId, rowLabelDraft.trim() || undefined);
        setRowLabelDraft('');
        setAddRowForTable(null);
        await refreshStructure();
    };

    const deleteRow = async (rowId: string) => {
        if (!window.confirm("Supprimer ce rang à partir d'aujourd'hui ? Il restera visible sur les jours déjà modifiés avant aujourd'hui.")) return;
        await roadmapApi.deleteRow(rowId);
        await refreshStructure();
        await refreshRotation();
    };

    // Édition d'une cellule de texte libre (tableaux à colonnes personnalisables) — chaque colonne de
    // type "text" est éditable indépendamment, remplace l'ancien champ unique "label" du rang.
    const startEditCell = (rowId: string, columnId: string, currentValue: string) => {
        setEditingCell({ rowId, columnId });
        setEditCellDraft(currentValue);
    };

    const saveCellValue = async (rowId: string, columnId: string) => {
        const value = editCellDraft;
        setEditingCell(null);
        updateSheetTables(activeSheetId, tables => tables.map(t => ({ ...t, rows: t.rows.map(r => r.id === rowId ? { ...r, cells: { ...r.cells, [columnId]: value } } : r) })));
        await roadmapApi.setCellValue(rowId, columnId, value);
    };

    const changeRowFontSize = async (row: RoadmapRow, delta: number) => {
        const fontSize = Math.min(28, Math.max(10, row.fontSize + delta));
        updateSheetTables(activeSheetId, tables => tables.map(t => ({ ...t, rows: t.rows.map(r => r.id === row.id ? { ...r, fontSize } : r) })));
        await roadmapApi.updateRowFontSize(row.id, fontSize);
    };

    // Réordonnancement des rangs par glisser-déposer natif — dans un même tableau, ou vers un autre
    // tableau du même service (demande d'Emeni : "une ligne d'un tableau je devrais pouvoir la
    // déplacer dans un autre tableau"). Les rangs se décalent en direct pendant le survol ; la
    // sauvegarde côté serveur n'a lieu qu'une fois, à la fin du glisser (onDragEnd).
    const rowDragRef = useRef<{ rowId: string } | null>(null);
    const [draggingRowId, setDraggingRowId] = useState<string | null>(null);
    const handleRowDragStart = (e: React.DragEvent, rowId: string) => {
        rowDragRef.current = { rowId };
        setDraggingRowId(rowId);
        e.dataTransfer.effectAllowed = 'move';
    };
    // Retire le rang glissé d'où qu'il se trouve actuellement et le réinsère dans `tableId`, juste
    // avant `beforeRowId` (ou en fin de tableau si `beforeRowId` est null — tableau vide ou dépose
    // après le dernier rang).
    const moveDraggedRow = (tableId: string, beforeRowId: string | null) => {
        const drag = rowDragRef.current;
        if (!drag || !activeSheetId) return;
        let movedRow: RoadmapRow | null = null;
        updateSheetTables(activeSheetId, tables => {
            const withoutMoved = tables.map(t => {
                const idx = t.rows.findIndex(r => r.id === drag.rowId);
                if (idx === -1) return t;
                movedRow = t.rows[idx];
                const rows = [...t.rows];
                rows.splice(idx, 1);
                return { ...t, rows };
            });
            if (!movedRow) return tables;
            return withoutMoved.map(t => {
                if (t.id !== tableId) return t;
                const rows = [...t.rows];
                const insertIdx = beforeRowId ? rows.findIndex(r => r.id === beforeRowId) : -1;
                if (insertIdx === -1) rows.push(movedRow as RoadmapRow);
                else rows.splice(insertIdx, 0, movedRow as RoadmapRow);
                return { ...t, rows };
            });
        });
    };
    const handleRowDragOverRow = (e: React.DragEvent, tableId: string, targetRowId: string) => {
        e.preventDefault();
        e.stopPropagation();
        const drag = rowDragRef.current;
        if (!drag || drag.rowId === targetRowId) return;
        moveDraggedRow(tableId, targetRowId);
    };
    // Survol du corps d'un tableau (pas d'un rang précis) : dépose en fin de ce tableau — utile pour
    // un tableau vide ou pour déposer après le dernier rang.
    const handleRowDragOverTableBody = (e: React.DragEvent, tableId: string) => {
        if (!rowDragRef.current) return;
        e.preventDefault();
        moveDraggedRow(tableId, null);
    };
    const handleRowDrop = (e: React.DragEvent) => { e.preventDefault(); };
    const handleRowDragEnd = async () => {
        const drag = rowDragRef.current;
        rowDragRef.current = null;
        setDraggingRowId(null);
        if (!drag) return;
        const destTable = (activeSheet?.tables || []).find(t => t.rows.some(r => r.id === drag.rowId));
        if (!destTable) return;
        await roadmapApi.moveRow(drag.rowId, destTable.id, destTable.rows.map(r => r.id));
    };

    // Plusieurs salariés assignables sur un même rang (demande d'Emeni) : on bascule l'appartenance
    // au lieu de remplacer une valeur unique. Par colonne, un tableau pouvant avoir plusieurs colonnes
    // d'assignation.
    const toggleAssign = async (rowId: string, columnId: string, employeeId: string) => {
        const current = assignments[rowId]?.[columnId] || [];
        const next = current.includes(employeeId) ? current.filter(id => id !== employeeId) : [...current, employeeId];
        setAssignments(prev => ({ ...prev, [rowId]: { ...prev[rowId], [columnId]: next } }));
        setModifiedToday(true);
        await roadmapApi.setAssignment(rowId, columnId, dateStr, next);
        await refreshRotation();
    };

    const clearAssign = async (rowId: string, columnId: string) => {
        setAssignments(prev => ({ ...prev, [rowId]: { ...prev[rowId], [columnId]: [] } }));
        setModifiedToday(true);
        await roadmapApi.setAssignment(rowId, columnId, dateStr, []);
        await refreshRotation();
    };

    const saveTitleLabel = async () => {
        const val = titleLabelDraft.trim() || 'Feuille de route';
        setTitleLayout(prev => ({ ...prev, label: val }));
        setEditingTitleLabel(false);
        await roadmapApi.saveSettings({ titleLabel: val });
    };

    const changeTitleFontSize = async (delta: number) => {
        const titleFontSize = Math.min(48, Math.max(12, titleLayout.fontSize + delta));
        setTitleLayout(prev => ({ ...prev, fontSize: titleFontSize }));
        await roadmapApi.saveSettings({ titleFontSize });
    };

    const changeMetaFontSize = async (delta: number) => {
        const metaFontSize = Math.min(24, Math.max(8, metaLayout.fontSize + delta));
        setMetaLayout(prev => ({ ...prev, fontSize: metaFontSize }));
        await roadmapApi.saveSettings({ metaFontSize });
    };

    // --- Zones de texte libres ---
    const addTextBox = async () => {
        if (!activeSheetId) return;
        const res = await roadmapApi.createTextBox(activeSheetId, 'Texte');
        if (res?.id) await refreshTextBoxes();
    };

    // Copie toute la structure d'une sous-feuille vers l'autre (Midi ↔ Soir) À L'INTÉRIEUR de la même
    // feuille de route — comportement d'origine, restauré tel quel après une première tentative trop
    // simplifiée : "on doit garder cette logique d'avant avec le bouton midi/soir [et] le copier vers
    // le soir" (demande d'Emeni). Uniquement pertinent quand la feuille de route active est en "2 feuilles".
    const copyToOtherService = async () => {
        if (!activeBoard || activeBoard.sheetMode !== 'split' || !activeSheet) return;
        const otherService: 'midi' | 'soir' = activeService === 'midi' ? 'soir' : 'midi';
        const otherSheet = activeBoard.sheets.find(s => s.service === otherService);
        if (!otherSheet) return;
        const otherLabel = otherService === 'midi' ? 'Midi' : 'Soir';
        const currentLabel = activeService === 'midi' ? 'Midi' : 'Soir';
        const confirmed = window.confirm(
            `Copier toute la structure (tableaux, rangs, zones de texte) et les affectations d'aujourd'hui de ${activeBoard.name} — ${currentLabel} vers ${activeBoard.name} — ${otherLabel} ?\n\nLa structure actuelle de ${otherLabel} sera entièrement remplacée.`
        );
        if (!confirmed) return;
        setCopyingService(true);
        try {
            await roadmapApi.copySheet(activeSheet.id, otherSheet.id, dateStr);
            // La structure/zones de texte/affectations de la sous-feuille de destination ont changé côté
            // serveur (nouveaux tableaux, nouveaux rangs, nouvelles tailles/positions) — sans ce
            // rafraîchissement le state restait périmé jusqu'au prochain rechargement complet de la
            // page, donnant l'impression que la copie n'avait pas repris la taille/l'espacement/les rangs.
            await Promise.all([refreshStructure(), refreshTextBoxes(), refreshAssignments()]);
            setActiveService(otherService);
        } finally {
            setCopyingService(false);
        }
        window.alert(`Feuille ${otherLabel} mise à jour à partir de ${currentLabel}.`);
    };

    const saveTextBoxContent = async (id: string, content: string) => {
        setTextBoxes(prev => prev.map(t => t.id === id ? { ...t, content } : t));
        await roadmapApi.updateTextBox(id, { content });
    };

    const deleteTextBox = async (id: string) => {
        if (!window.confirm('Supprimer cette zone de texte ?')) return;
        await roadmapApi.deleteTextBox(id);
        await refreshTextBoxes();
    };

    const duplicateTextBox = async (id: string) => {
        await roadmapApi.duplicateTextBox(id);
        await refreshTextBoxes();
    };

    const changeTextBoxFontSize = async (box: TextBox, delta: number) => {
        const fontSize = Math.min(48, Math.max(10, box.fontSize + delta));
        setTextBoxes(prev => prev.map(t => t.id === box.id ? { ...t, fontSize } : t));
        await roadmapApi.updateTextBox(box.id, { fontSize });
    };

    const toggleTextBoxBold = async (box: TextBox) => {
        const bold = !box.bold;
        setTextBoxes(prev => prev.map(t => t.id === box.id ? { ...t, bold } : t));
        await roadmapApi.updateTextBox(box.id, { bold });
    };

    const toggleTextBoxItalic = async (box: TextBox) => {
        const italic = !box.italic;
        setTextBoxes(prev => prev.map(t => t.id === box.id ? { ...t, italic } : t));
        await roadmapApi.updateTextBox(box.id, { italic });
    };

    const setTextBoxAlign = async (box: TextBox, align: TextAlign) => {
        setTextBoxes(prev => prev.map(t => t.id === box.id ? { ...t, align } : t));
        await roadmapApi.updateTextBox(box.id, { align });
    };

    // --- Feuilles de route (onglets façon Excel) ---
    const addBoard = async () => {
        const name = newBoardNameDraft.trim();
        if (!name) return;
        const res = await roadmapApi.createBoard(name, newBoardSheetMode);
        setAddingBoard(false);
        setNewBoardNameDraft('');
        setNewBoardSheetMode('split');
        if (res?.id) {
            await refreshStructure();
            setActiveBoardId(res.id);
            setActiveService('midi');
        }
    };

    const saveBoardName = async (id: string) => {
        const name = boardNameDraft.trim();
        setEditingBoardId(null);
        if (!name) return;
        setStructure(prev => ({ boards: prev.boards.map(b => b.id === id ? { ...b, name } : b) }));
        await roadmapApi.updateBoard(id, { name });
    };

    // Bascule 1 feuille ↔ 2 feuilles pour la feuille de route active — remplace l'ancien réglage global
    // "Feuilles" (Mise en page), désormais par feuille de route (demande d'Emeni). Passer à "2 feuilles"
    // peut créer la sous-feuille Soir manquante côté serveur, d'où le rechargement de la structure.
    const saveBoardSheetMode = async (mode: 'split' | 'unified') => {
        if (!activeBoardId) return;
        if (mode === 'unified') setActiveService('midi');
        await roadmapApi.updateBoard(activeBoardId, { sheetMode: mode });
        await refreshStructure();
    };

    const deleteBoard = async (id: string) => {
        const board = structure.boards.find(b => b.id === id);
        if (!board) return;
        if (!window.confirm(`Supprimer définitivement la feuille de route "${board.name}" et tout son contenu (tableaux, rangs, affectations) ? Cette action est irréversible.`)) return;
        await roadmapApi.deleteBoard(id);
        await Promise.all([refreshStructure(), refreshTextBoxes()]);
    };

    // --- Mise en page (orientation / marges) ---
    const savePageSettings = async (patch: Partial<PageSettings>) => {
        const next = { ...pageSettings, ...patch };
        setPageSettings(next);
        await roadmapApi.saveSettings(patch as any);
    };

    const toggleDayPreview = async () => {
        const next = !showDayPreview;
        setShowDayPreview(next);
        await roadmapApi.saveSettings({ showDayPreview: next });
    };

    const toggleAssignShowAllEmployees = async () => {
        const next = !assignShowAllEmployees;
        setAssignShowAllEmployees(next);
        await roadmapApi.saveSettings({ assignShowAllEmployees: next });
    };

    // Sauvegarde un créneau modifié depuis l'aperçu planning — écrit dans la même semaine (planningApi.
    // saveWeek) que le module Planning, donc visible là-bas immédiatement, pas une copie locale.
    const handleDayPreviewShiftUpdate = async (updatedShift: Shift) => {
        if (!planningWeek || !editingDayPreviewShift) return;
        const newRows = planningWeek.rows.map(row =>
            row.employeeId === editingDayPreviewShift.employeeId
                ? { ...row, shifts: { ...row.shifts, [editingDayPreviewShift.date]: updatedShift } }
                : row
        );
        const updated = { ...planningWeek, rows: newRows, weekStart: mondayStr };
        setPlanningWeek(updated);
        setEditingDayPreviewShift(null);
        try {
            await planningApi.saveWeek(updated);
        } catch (e) {
            console.error('Failed to save shift from day preview:', e);
        }
        await refreshAssignments();
    };

    const saveTierColor = async (tier: RangTier, color: string) => {
        setTierColors(prev => ({ ...prev, [tier]: color }));
        const key = tier === 'never' ? 'tierColorNever' : tier === 'low' ? 'tierColorLow' : 'tierColorOk';
        await roadmapApi.saveSettings({ [key]: color } as any);
    };

    // --- Drag / resize génériques (tableaux, zones de texte, titre) ---
    const getPos = (kind: DragKind, id: string) => {
        if (kind === 'table') { const t = (activeSheet?.tables || []).find(t => t.id === id); return t ? { x: t.posX, y: t.posY } : { x: 0, y: 0 }; }
        if (kind === 'textbox') { const t = activeTextBoxes.find(t => t.id === id); return t ? { x: t.posX, y: t.posY } : { x: 0, y: 0 }; }
        if (kind === 'meta') return { x: metaLayout.posX, y: metaLayout.posY };
        return { x: titleLayout.posX, y: titleLayout.posY };
    };

    const startDrag = (e: React.MouseEvent, kind: DragKind, id: string) => {
        e.preventDefault();
        const { x, y } = getPos(kind, id);
        const key = `${kind}:${id}`;
        const selfEl = nodeRefs.current[key];
        const w = selfEl?.offsetWidth ?? MIN_WIDTH;
        const h = selfEl?.offsetHeight ?? MIN_TEXTBOX_HEIGHT;
        const siblings: Box[] = [];
        Object.entries(nodeRefs.current).forEach(([k, el]) => {
            if (k === key || !el) return;
            const sx = parseFloat(el.style.left) || 0;
            const sy = parseFloat(el.style.top) || 0;
            siblings.push({ x: sx, y: sy, w: el.offsetWidth, h: el.offsetHeight });
        });
        dragRef.current = { kind, id, startX: e.clientX, startY: e.clientY, origX: x, origY: y, w, h, siblings, curX: x, curY: y };
        window.addEventListener('mousemove', onDragMove);
        window.addEventListener('mouseup', onDragEnd);
    };
    // Repères d'alignement façon Canva : centre de page (les deux axes) + alignement avec un autre
    // élément déjà posé (même haut / même centre) — permet notamment d'aligner deux tableaux sur la
    // même ligne de hauteur. Le repère "aimante" légèrement la position, pas juste un affichage.
    // La position finale est aussi écrite dans la ref (curX/curY), pas seulement dans le state React :
    // le handler onDragEnd est enregistré une seule fois au début du glisser et garderait sinon une
    // closure périmée sur le state d'avant le déplacement (la position sauvegardée reviendrait en arrière).
    const onDragMove = (e: MouseEvent) => {
        const d = dragRef.current;
        if (!d) return;
        let posX = Math.max(0, d.origX + (e.clientX - d.startX));
        let posY = Math.max(0, d.origY + (e.clientY - d.startY));
        const { w, h } = d;
        const newGuides: Guide[] = [];

        const pageCenterX = pageWidthPx / 2;
        const pageCenterY = pageHeightPx / 2;
        if (Math.abs((posX + w / 2) - pageCenterX) < SNAP_THRESHOLD) {
            posX = pageCenterX - w / 2;
            newGuides.push({ type: 'v', pos: pageCenterX });
        }
        if (Math.abs((posY + h / 2) - pageCenterY) < SNAP_THRESHOLD) {
            posY = pageCenterY - h / 2;
            newGuides.push({ type: 'h', pos: pageCenterY });
        }

        d.siblings.forEach(s => {
            if (Math.abs(posX - s.x) < SNAP_THRESHOLD) { posX = s.x; newGuides.push({ type: 'v', pos: s.x }); }
            else if (Math.abs((posX + w / 2) - (s.x + s.w / 2)) < SNAP_THRESHOLD) { posX = s.x + s.w / 2 - w / 2; newGuides.push({ type: 'v', pos: s.x + s.w / 2 }); }

            if (Math.abs(posY - s.y) < SNAP_THRESHOLD) { posY = s.y; newGuides.push({ type: 'h', pos: s.y }); }
            else if (Math.abs((posY + h / 2) - (s.y + s.h / 2)) < SNAP_THRESHOLD) { posY = s.y + s.h / 2 - h / 2; newGuides.push({ type: 'h', pos: s.y + s.h / 2 }); }
        });

        d.curX = posX;
        d.curY = posY;
        setGuides(newGuides);
        if (d.kind === 'table') updateSheetTables(activeSheetId, tables => tables.map(t => t.id === d.id ? { ...t, posX, posY } : t));
        else if (d.kind === 'textbox') setTextBoxes(prev => prev.map(t => t.id === d.id ? { ...t, posX, posY } : t));
        else if (d.kind === 'meta') setMetaLayout(prev => ({ ...prev, posX, posY }));
        else setTitleLayout(prev => ({ ...prev, posX, posY }));
    };
    const onDragEnd = async () => {
        const d = dragRef.current;
        dragRef.current = null;
        window.removeEventListener('mousemove', onDragMove);
        window.removeEventListener('mouseup', onDragEnd);
        setGuides([]);
        if (!d) return;
        if (d.kind === 'table') await roadmapApi.updateTableLayout(d.id, { posX: d.curX, posY: d.curY });
        else if (d.kind === 'textbox') await roadmapApi.updateTextBox(d.id, { posX: d.curX, posY: d.curY });
        else if (d.kind === 'meta') await roadmapApi.saveSettings({ metaPosX: d.curX, metaPosY: d.curY });
        else await roadmapApi.saveSettings({ titlePosX: d.curX, titlePosY: d.curY });
    };

    const startResize = (e: React.MouseEvent, kind: DragKind, id: string) => {
        e.preventDefault();
        e.stopPropagation();
        let origWidth = MIN_WIDTH, origHeight = MIN_TEXTBOX_HEIGHT;
        let origRowFontSizes: Record<string, number> | undefined;
        let origTitleFontSize: number | undefined;
        const { x: posX, y: posY } = getPos(kind, id);
        if (kind === 'table') {
            const t = (activeSheet?.tables || []).find(t => t.id === id);
            origWidth = t?.width ?? MIN_WIDTH;
            // Point de départ du redimensionnement en hauteur = la hauteur RÉELLEMENT rendue (contenu +
            // éventuelle hauteur minimale déjà posée), pour que le glisser démarre sans saut visuel —
            // demande d'Emeni : pouvoir "allonger" un tableau, pas seulement l'élargir.
            origHeight = nodeRefs.current[`table:${id}`]?.offsetHeight ?? MIN_TABLE_HEIGHT;
            origRowFontSizes = {};
            (t?.rows || []).forEach(row => { origRowFontSizes![row.id] = row.fontSize; });
            origTitleFontSize = t?.titleFontSize;
        }
        else if (kind === 'textbox') {
            const t = activeTextBoxes.find(t => t.id === id);
            origWidth = t?.width ?? MIN_WIDTH; origHeight = t?.height ?? MIN_TEXTBOX_HEIGHT;
        } else origWidth = titleLayout.width;

        // Boîtes des autres tableaux/zones de texte (position + taille RÉELLEMENT rendue), pour les
        // repères d'alignement de taille pendant le redimensionnement.
        const key = `${kind}:${id}`;
        const siblings: Box[] = [];
        Object.entries(nodeRefs.current).forEach(([k, el]) => {
            if (k === key || !el || !k.startsWith('table:') && !k.startsWith('textbox:')) return;
            const sx = parseFloat(el.style.left) || 0;
            const sy = parseFloat(el.style.top) || 0;
            siblings.push({ x: sx, y: sy, w: el.offsetWidth, h: el.offsetHeight });
        });

        resizeRef.current = {
            kind, id, startX: e.clientX, startY: e.clientY, origWidth, origHeight, curWidth: origWidth, curHeight: origHeight,
            origRowFontSizes, curRowFontSizes: origRowFontSizes ? { ...origRowFontSizes } : undefined,
            origTitleFontSize, curTitleFontSize: origTitleFontSize,
            posX, posY, siblings,
        };
        window.addEventListener('mousemove', onResizeMove);
        window.addEventListener('mouseup', onResizeEnd);
    };
    const onResizeMove = (e: MouseEvent) => {
        const r = resizeRef.current;
        if (!r) return;
        let width = Math.max(MIN_WIDTH, r.origWidth + (e.clientX - r.startX));
        let height = Math.max(r.kind === 'table' ? MIN_TABLE_HEIGHT : MIN_TEXTBOX_HEIGHT, r.origHeight + (e.clientY - r.startY));

        // Repères de taille façon Canva, comme pour le déplacement : largeur ET hauteur s'aimantent sur
        // celles d'un tableau/zone de texte déjà posé quand on s'en approche, pour ne plus jamais se
        // retrouver à 1px près d'une taille voulue identique (ex. deux tableaux côte à côte). Un tableau
        // peut désormais être "allongé" librement (pas seulement élargi) — demande d'Emeni ; la hauteur
        // ne descend jamais en dessous de ce que le contenu (rangs + police) exige réellement, géré côté
        // CSS (min-height), pas ici.
        const newGuides: Guide[] = [];
        r.siblings.forEach(s => {
            if (Math.abs(width - s.w) < SNAP_THRESHOLD) {
                width = s.w;
                newGuides.push({ type: 'v', pos: r.posX + width });
            } else if (Math.abs((r.posX + width) - (s.x + s.w)) < SNAP_THRESHOLD) {
                width = s.x + s.w - r.posX;
                newGuides.push({ type: 'v', pos: s.x + s.w });
            }
            if (Math.abs(height - s.h) < SNAP_THRESHOLD) {
                height = s.h;
                newGuides.push({ type: 'h', pos: r.posY + height });
            } else if (Math.abs((r.posY + height) - (s.y + s.h)) < SNAP_THRESHOLD) {
                height = s.y + s.h - r.posY;
                newGuides.push({ type: 'h', pos: s.y + s.h });
            }
        });

        r.curWidth = width;
        r.curHeight = height;
        if (r.kind === 'table') {
            // La taille de la police des rangs et du titre suit proportionnellement la largeur (pas la
            // hauteur, qui n'ajoute que de l'espace vide en bas sans rien redimensionner d'autre).
            const scale = width / r.origWidth;
            const scaledRowFontSizes: Record<string, number> = {};
            if (r.origRowFontSizes) {
                for (const rowId in r.origRowFontSizes) {
                    scaledRowFontSizes[rowId] = Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, Math.round(r.origRowFontSizes[rowId] * scale)));
                }
            }
            const scaledTitleFontSize = r.origTitleFontSize != null
                ? Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, Math.round(r.origTitleFontSize * scale)))
                : undefined;
            r.curRowFontSizes = scaledRowFontSizes;
            r.curTitleFontSize = scaledTitleFontSize;
            updateSheetTables(activeSheetId, tables => tables.map(t => t.id === r.id
                ? { ...t, width, minHeight: height, titleFontSize: scaledTitleFontSize ?? t.titleFontSize, rows: t.rows.map(row => ({ ...row, fontSize: scaledRowFontSizes[row.id] ?? row.fontSize })) }
                : t));
        }
        else if (r.kind === 'textbox') setTextBoxes(prev => prev.map(t => t.id === r.id ? { ...t, width, height } : t));
        else setTitleLayout(prev => ({ ...prev, width }));
        setGuides(newGuides);
    };
    const onResizeEnd = async () => {
        const r = resizeRef.current;
        resizeRef.current = null;
        window.removeEventListener('mousemove', onResizeMove);
        window.removeEventListener('mouseup', onResizeEnd);
        setGuides([]);
        if (!r) return;
        if (r.kind === 'table') {
            await Promise.all([
                roadmapApi.updateTableLayout(r.id, { width: r.curWidth, minHeight: r.curHeight, ...(r.curTitleFontSize != null ? { titleFontSize: r.curTitleFontSize } : {}) }),
                ...Object.entries(r.curRowFontSizes || {}).map(([rowId, fontSize]) => roadmapApi.updateRowFontSize(rowId, fontSize)),
            ]);
        } else if (r.kind === 'textbox') {
            await roadmapApi.updateTextBox(r.id, { width: r.curWidth, height: r.curHeight });
        } else {
            await roadmapApi.saveSettings({ titleWidth: titleLayout.width });
        }
    };

    const changeTableTitleFontSize = async (table: RoadmapTable, delta: number) => {
        const titleFontSize = Math.min(28, Math.max(10, table.titleFontSize + delta));
        updateSheetTables(activeSheetId, tables => tables.map(t => t.id === table.id ? { ...t, titleFontSize } : t));
        await roadmapApi.updateTableLayout(table.id, { titleFontSize });
    };

    const startEditHeader = (columnId: string, current: string) => {
        setEditingColumnId(columnId);
        setHeaderDraft(current);
    };

    const saveColumnHeader = async (tableId: string, columnId: string) => {
        const value = headerDraft.trim() || 'Colonne';
        setEditingColumnId(null);
        updateSheetTables(activeSheetId, tables => tables.map(t => t.id === tableId
            ? { ...t, columns: t.columns.map(col => col.id === columnId ? { ...col, header: value } : col) }
            : t));
        await roadmapApi.renameColumn(columnId, value);
    };

    const handlePrint = async () => {
        if (!activeSheet) return;
        const tables = activeSheet.tables;
        // Hauteur réellement affichée à l'écran pour chaque tableau — mesurée sur le DOM plutôt que
        // recalculée à la main, pour que le PDF occupe exactement le même espace vertical et que
        // l'écart avant le tableau suivant (positions fixes) reste identique entre écran et PDF.
        // Remonté par Emeni : "les espacements ne sont pas les mêmes" entre l'écran et le PDF exporté.
        const measuredHeightPxByTableId: Record<string, number> = {};
        tables.forEach(t => {
            const el = nodeRefs.current[`table:${t.id}`];
            if (el) measuredHeightPxByTableId[t.id] = el.getBoundingClientRect().height;
        });
        const boardLabel = activeBoard?.sheetMode === 'split' ? `${activeBoard.name} — ${activeService === 'midi' ? 'Midi' : 'Soir'}` : (activeBoard?.name || 'Feuille de route');
        const doc = await generateRoadmapPDF({
            companyName: user?.company_name || 'Votre Établissement',
            brandColor: user?.theme_color || '#987b55',
            logoUrl: user?.logo_url || (user as any)?.logoUrl || null,
            serviceLabel: boardLabel,
            date,
            orientation: pageSettings.orientation,
            margins: { top: pageSettings.marginTop, right: pageSettings.marginRight, bottom: pageSettings.marginBottom, left: pageSettings.marginLeft },
            title: titleLayout,
            meta: metaLayout,
            tables: tables.map(t => ({ id: t.id, title: t.title, posX: t.posX, posY: t.posY, width: t.width, titleFontSize: t.titleFontSize, columns: t.columns, rows: t.rows, measuredHeightPx: measuredHeightPxByTableId[t.id] })),
            textBoxes: activeTextBoxes,
            assignments,
            employeeNameById: employeeFirstNameById,
        });
        doc.save(`feuille-de-route-${boardLabel.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/[^a-z0-9]+/g, '-')}-${dateStr}.pdf`);
    };

    // Ctrl+C / Ctrl+V sur le dernier tableau ou zone de texte cliqué — ignoré si le focus est dans un
    // champ de saisie pour ne pas gêner le copier-coller natif du texte (renommage, contenu, etc.).
    // Ctrl+C mémorise l'élément dans un vrai presse-papiers (persiste au changement de feuille de
    // route/Midi-Soir) ; Ctrl+V le colle sur la feuille active au moment du collage, permettant de
    // copier un tableau d'une feuille de route vers une autre (demande d'Emeni).
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            const key = e.key.toLowerCase();
            if (key !== 'c' && key !== 'v') return;
            const active = document.activeElement;
            const tag = active?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || (active as HTMLElement)?.isContentEditable) return;
            if (key === 'c') {
                if (!selectedElement) return;
                e.preventDefault();
                setClipboard(selectedElement);
                return;
            }
            if (!clipboard) return;
            e.preventDefault();
            if (clipboard.kind === 'table') duplicateTable(clipboard.id, activeSheetId || undefined);
            else duplicateTextBox(clipboard.id);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [selectedElement, clipboard, activeSheetId, dateStr]);

    // Mémoïsés : un tableau littéral `[]`/`.filter()` recréerait une nouvelle référence à chaque rendu,
    // ce qui ferait boucler indéfiniment le useLayoutEffect des repères de rang (dépendant de activeTables)
    // tant qu'aucune feuille n'est encore chargée.
    const activeTables = useMemo(() => activeSheet?.tables ?? EMPTY_TABLES, [activeSheet]);
    const activeTextBoxes = useMemo(() => textBoxes.filter(t => t.sheetId === activeSheetId), [textBoxes, activeSheetId]);

    // Repères visuels reliant les rangs assignés au même salarié, mesurés sur le DOM (les rangs ont des
    // hauteurs variables selon leur taille de police) après chaque rendu concerné — demande d'Emeni.
    // Deux salariés dont les rangs se chevauchent en hauteur ne doivent jamais se croiser visuellement
    // (remonté par Emeni, capture à l'appui) : chaque groupe est placé dans le premier "couloir"
    // (lane) vertical libre, comme un diagramme de dépendances — un couloir ne contient jamais deux
    // plages qui se chevauchent.
    useLayoutEffect(() => {
        const next: Record<string, { top: number; bottom: number; dots: number[]; color: string; lane: number }[]> = {};
        const lanes: Record<string, number> = {};
        activeTables.forEach(table => {
            const bodyEl = rowBodyRefs.current[table.id];
            if (!bodyEl) return;
            const bodyTop = bodyEl.getBoundingClientRect().top;
            const rowCenterById: Record<string, number> = {};
            table.rows.forEach(row => {
                const el = rowNodeRefs.current[row.id];
                if (el) rowCenterById[row.id] = el.getBoundingClientRect().top - bodyTop + el.offsetHeight / 2;
            });

            const rowIdsByEmployee: Record<string, string[]> = {};
            table.columns.filter(col => col.type === 'assign').forEach(col => {
                table.rows.forEach(row => {
                    (assignments[row.id]?.[col.id] || []).forEach(empId => {
                        (rowIdsByEmployee[empId] ||= []).push(row.id);
                    });
                });
            });

            const groups: { top: number; bottom: number; dots: number[]; color: string }[] = [];
            Object.entries(rowIdsByEmployee).forEach(([empId, rowIds]) => {
                if (rowIds.length < 2) return;
                const centers = rowIds.map(id => rowCenterById[id]).filter(y => y !== undefined).sort((a, b) => a - b);
                if (centers.length < 2) return;
                groups.push({ top: centers[0], bottom: centers[centers.length - 1], dots: centers, color: colorForEmployee(empId) });
            });
            groups.sort((a, b) => a.top - b.top);
            const laneEnds: number[] = [];
            const placed = groups.map(g => {
                let lane = laneEnds.findIndex(end => end < g.top);
                if (lane === -1) { lane = laneEnds.length; laneEnds.push(g.bottom); } else { laneEnds[lane] = g.bottom; }
                return { ...g, lane };
            });
            if (placed.length) { next[table.id] = placed; lanes[table.id] = laneEnds.length; }
        });
        setConnectorsByTable(next);
        setConnectorLanesByTable(lanes);
    }, [activeTables, assignments]);

    const pageDims = A4[pageSettings.orientation];
    const pageWidthPx = pageDims.w * PX_PER_MM;
    const pageHeightPx = pageDims.h * PX_PER_MM;
    const marginTopPx = pageSettings.marginTop * PX_PER_MM;
    const marginRightPx = pageSettings.marginRight * PX_PER_MM;
    const marginBottomPx = pageSettings.marginBottom * PX_PER_MM;
    const marginLeftPx = pageSettings.marginLeft * PX_PER_MM;

    const lowestContentY = Math.max(
        marginTopPx + 100,
        ...activeTables.map(t => t.posY + 220),
        ...activeTextBoxes.map(t => t.posY + t.height),
        0
    );
    const pageCount = Math.max(1, Math.ceil(lowestContentY / pageHeightPx));
    const canvasHeight = Math.max(pageHeightPx * pageCount, lowestContentY + 80);

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-[var(--bg-page)] p-4 lg:p-6">
            <header className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/planning')} className="p-2 bg-white dark:bg-[var(--bg-card)] border border-slate-200 dark:border-border rounded-xl text-slate-500 hover:text-slate-700 dark:text-slate-300" title="Retour au Planning">
                        <ArrowLeft size={18} />
                    </button>
                    {(user?.logo_url || (user as any)?.logoUrl) && (
                        <img src={user?.logo_url || (user as any)?.logoUrl} alt="Logo" className="h-10 w-10 rounded-lg object-cover border border-slate-200 dark:border-border" />
                    )}
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full text-xs font-black uppercase tracking-widest bg-brand-100 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">{activeBoard?.name || 'Feuille de route'}</span>
                            {activeBoard?.sheetMode === 'split' && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-black uppercase tracking-widest ${activeService === 'midi' ? 'bg-orange-100 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400' : 'bg-blue-100 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400'}`}>
                                    {activeService === 'midi' ? 'Midi' : 'Soir'}
                                </span>
                            )}
                        </div>
                        <p className="text-sm font-bold text-brand-500 capitalize mt-0.5">{format(date, 'EEEE d MMMM yyyy', { locale: fr })}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {activeBoard?.sheetMode === 'split' && (
                        <div className="flex bg-slate-100 dark:bg-[var(--bg-soft)] p-1 rounded-lg">
                            <button onClick={() => setActiveService('midi')} className={`px-3 py-1.5 text-sm font-bold rounded-md transition-all ${activeService === 'midi' ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-brand-500' : 'text-slate-500 dark:text-slate-400'}`}>Midi</button>
                            <button onClick={() => setActiveService('soir')} className={`px-3 py-1.5 text-sm font-bold rounded-md transition-all ${activeService === 'soir' ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-brand-500' : 'text-slate-500 dark:text-slate-400'}`}>Soir</button>
                        </div>
                    )}
                    <div className="flex items-center gap-1 bg-white dark:bg-[var(--bg-card)] border border-slate-200 dark:border-border rounded-xl px-1">
                        <button onClick={() => setDate(d => addDays(d, -1))} className="p-2 text-slate-500 hover:text-brand-500"><ChevronLeft size={18} /></button>
                        <input
                            type="date"
                            value={dateStr}
                            onChange={(e) => e.target.value && setDate(parseISO(e.target.value))}
                            className="bg-transparent text-sm font-semibold text-slate-700 dark:text-content outline-none"
                        />
                        <button onClick={() => setDate(d => addDays(d, 1))} className="p-2 text-slate-500 hover:text-brand-500"><ChevronRight size={18} /></button>
                    </div>

                    <button
                        onClick={toggleDayPreview}
                        className={`flex items-center gap-1 px-3 py-2 border rounded-xl text-sm font-semibold ${showDayPreview ? 'bg-brand-50 dark:bg-brand-500/10 border-brand-300 dark:border-brand-500/40 text-brand-600' : 'border-slate-200 dark:border-border text-slate-600 dark:text-content hover:bg-slate-50 dark:hover:bg-surface-soft'}`}
                        title="Afficher/masquer l'aperçu du planning du jour"
                    >
                        {showDayPreview ? <Eye size={16} /> : <EyeOff size={16} />} Aperçu planning
                    </button>

                    <button
                        onClick={toggleAssignShowAllEmployees}
                        className={`flex items-center gap-1 px-3 py-2 border rounded-xl text-sm font-semibold ${assignShowAllEmployees ? 'bg-brand-50 dark:bg-brand-500/10 border-brand-300 dark:border-brand-500/40 text-brand-600' : 'border-slate-200 dark:border-border text-slate-600 dark:text-content hover:bg-slate-50 dark:hover:bg-surface-soft'}`}
                        title="Liste d'assignation : tous les salariés, ou seulement ceux prévus ce jour-là"
                    >
                        <Users size={16} /> Tous les salariés
                    </button>

                    <div className="relative">
                        <button onClick={() => setPageMenuOpen(v => !v)} className="flex items-center gap-1 px-3 py-2 border border-slate-200 dark:border-border text-slate-600 dark:text-content rounded-xl text-sm font-semibold hover:bg-slate-50 dark:hover:bg-surface-soft">
                            <LayoutGrid size={16} /> Mise en page
                        </button>
                        {pageMenuOpen && (
                            <>
                                <div className="fixed inset-0 z-30" onClick={() => setPageMenuOpen(false)} />
                                <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-[var(--bg-card)] border border-slate-200 dark:border-border rounded-xl shadow-xl p-4 z-40 space-y-3">
                                <div>
                                    <p className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">Feuilles de "{activeBoard?.name || ''}"</p>
                                    <div className="flex bg-slate-100 dark:bg-[var(--bg-soft)] p-1 rounded-lg">
                                        <button onClick={() => saveBoardSheetMode('split')} className={`flex-1 px-2 py-1.5 text-xs font-bold rounded-md ${activeBoard?.sheetMode === 'split' ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-brand-500' : 'text-slate-500'}`}>2 feuilles (Midi / Soir)</button>
                                        <button onClick={() => saveBoardSheetMode('unified')} className={`flex-1 px-2 py-1.5 text-xs font-bold rounded-md ${activeBoard?.sheetMode === 'unified' ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-brand-500' : 'text-slate-500'}`}>1 feuille (Midi + Soir)</button>
                                    </div>
                                    <p className="text-[11px] text-slate-400 mt-1">En 1 feuille, les salariés assignables sont ceux prévus le midi OU le soir ce jour-là.</p>
                                </div>
                                <div>
                                    <p className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">Orientation</p>
                                    <div className="flex bg-slate-100 dark:bg-[var(--bg-soft)] p-1 rounded-lg">
                                        <button onClick={() => savePageSettings({ orientation: 'portrait' })} className={`flex-1 px-3 py-1.5 text-sm font-bold rounded-md ${pageSettings.orientation === 'portrait' ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-brand-500' : 'text-slate-500'}`}>Portrait</button>
                                        <button onClick={() => savePageSettings({ orientation: 'landscape' })} className={`flex-1 px-3 py-1.5 text-sm font-bold rounded-md ${pageSettings.orientation === 'landscape' ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-brand-500' : 'text-slate-500'}`}>Paysage</button>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">Marges (mm)</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {([['marginTop', 'Haut'], ['marginRight', 'Droite'], ['marginBottom', 'Bas'], ['marginLeft', 'Gauche']] as const).map(([key, label]) => (
                                            <label key={key} className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                                                {label}
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={60}
                                                    value={pageSettings[key]}
                                                    onChange={(e) => savePageSettings({ [key]: Number(e.target.value) || 0 } as Partial<PageSettings>)}
                                                    className="w-16 px-2 py-1 rounded-lg border border-slate-200 dark:border-border bg-slate-50 dark:bg-[var(--bg-soft)] text-slate-800 dark:text-content"
                                                />
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">Couleurs du roulement</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        {([['never', 'Jamais fait'], ['low', 'Pas assez'], ['ok', 'Habitué']] as const).map(([tier, label]) => (
                                            <label key={tier} className="flex flex-col items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400 text-center">
                                                {label}
                                                <input
                                                    type="color"
                                                    value={tierColors[tier]}
                                                    onChange={(e) => saveTierColor(tier, e.target.value)}
                                                    className="w-8 h-8 rounded-lg border border-slate-200 dark:border-border cursor-pointer bg-transparent"
                                                />
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-[11px] text-slate-400">Le cadre pointillé sur la feuille indique la limite d'impression (format A4).</p>
                            </div>
                            </>
                        )}
                    </div>

                    <button onClick={() => setAddTableOpen(true)} className="flex items-center gap-1 bg-brand-600 text-brand-fg px-3 py-2 rounded-xl text-sm font-semibold hover:brightness-95"><Plus size={16} /> Tableau</button>
                    <button onClick={addTextBox} className="flex items-center gap-1 px-3 py-2 border border-slate-200 dark:border-border text-slate-600 dark:text-content rounded-xl text-sm font-semibold hover:bg-slate-50 dark:hover:bg-surface-soft"><TypeIcon size={16} /> Texte</button>
                    {activeBoard?.sheetMode === 'split' && (
                        <button onClick={copyToOtherService} disabled={copyingService} className="flex items-center gap-1 px-3 py-2 border border-slate-200 dark:border-border text-slate-600 dark:text-content rounded-xl text-sm font-semibold hover:bg-slate-50 dark:hover:bg-surface-soft disabled:opacity-50" title={`Copier la structure et les affectations d'aujourd'hui vers ${activeService === 'midi' ? 'Soir' : 'Midi'}`}>
                            <Copy size={16} /> Copier vers {activeService === 'midi' ? 'Soir' : 'Midi'}
                        </button>
                    )}
                    <button onClick={handlePrint} className="p-2 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-border hover:bg-blue-50 dark:hover:bg-blue-500/10 hover:text-blue-600 rounded-xl" title="Imprimer"><Printer size={18} /></button>
                </div>
            </header>

            {/* Onglets de feuilles de route façon Excel (demande d'Emeni : "un onglet avec un petit + pour
                ajouter une nouvelle feuille de route... on doit pouvoir les nommer", ex. une feuille
                "Salle" et une feuille "Cuisine"). Chacune garde la logique Midi/Soir + Copier vers...
                d'origine à l'intérieur (bouton "Mise en page" pour choisir 1 ou 2 feuilles). */}
            <div className="flex items-center gap-1 mb-6 border-b border-slate-200 dark:border-border overflow-x-auto">
                {[...structure.boards].sort((a, b) => a.position - b.position).map(board => (
                    <div
                        key={board.id}
                        className={`group flex items-center gap-1.5 px-3 py-2 rounded-t-lg border border-b-0 cursor-pointer shrink-0 ${board.id === activeBoardId ? 'bg-white dark:bg-[var(--bg-card)] border-slate-200 dark:border-border text-brand-600 font-bold' : 'bg-slate-100 dark:bg-[var(--bg-soft)] border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-content'}`}
                        onClick={() => setActiveBoardId(board.id)}
                    >
                        {editingBoardId === board.id ? (
                            <input
                                autoFocus
                                value={boardNameDraft}
                                onChange={(e) => setBoardNameDraft(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onBlur={() => saveBoardName(board.id)}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveBoardName(board.id); if (e.key === 'Escape') setEditingBoardId(null); }}
                                className="w-24 text-sm bg-slate-50 dark:bg-[var(--bg-soft)] border border-brand-400 rounded px-1 outline-none"
                            />
                        ) : (
                            <span
                                className="text-sm"
                                onDoubleClick={(e) => { e.stopPropagation(); setEditingBoardId(board.id); setBoardNameDraft(board.name); }}
                                title="Double-cliquer pour renommer"
                            >
                                {board.name}
                            </span>
                        )}
                        {structure.boards.length > 1 && (
                            <button
                                onClick={(e) => { e.stopPropagation(); deleteBoard(board.id); }}
                                className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 shrink-0"
                                title="Supprimer cette feuille de route"
                            >
                                <X size={13} />
                            </button>
                        )}
                    </div>
                ))}
                {addingBoard ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                        <input
                            autoFocus
                            value={newBoardNameDraft}
                            onChange={(e) => setNewBoardNameDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') addBoard(); if (e.key === 'Escape') { setAddingBoard(false); setNewBoardNameDraft(''); } }}
                            placeholder="Nom de la feuille de route"
                            className="w-40 text-sm bg-slate-50 dark:bg-[var(--bg-soft)] border border-brand-400 rounded px-2 py-1.5 outline-none"
                        />
                        <div className="flex bg-slate-100 dark:bg-[var(--bg-soft)] p-0.5 rounded-lg shrink-0">
                            <button type="button" onClick={() => setNewBoardSheetMode('split')} title="2 feuilles (Midi / Soir)" className={`px-2 py-1 text-[11px] font-bold rounded-md ${newBoardSheetMode === 'split' ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-brand-500' : 'text-slate-500'}`}>2 feuilles</button>
                            <button type="button" onClick={() => setNewBoardSheetMode('unified')} title="1 feuille" className={`px-2 py-1 text-[11px] font-bold rounded-md ${newBoardSheetMode === 'unified' ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-brand-500' : 'text-slate-500'}`}>1 feuille</button>
                        </div>
                        <button onClick={addBoard} className="p-1 text-emerald-600 shrink-0"><Check size={16} /></button>
                        <button onClick={() => { setAddingBoard(false); setNewBoardNameDraft(''); }} className="p-1 text-slate-400 shrink-0"><X size={16} /></button>
                    </div>
                ) : (
                    <button
                        onClick={() => { setAddingBoard(true); setNewBoardNameDraft(''); setNewBoardSheetMode('split'); }}
                        className="p-2 text-slate-400 hover:text-brand-500 shrink-0"
                        title="Ajouter une feuille de route"
                    >
                        <Plus size={16} />
                    </button>
                )}
            </div>

            {!loading && planningWeek === null && (
                <div className="mb-4 px-4 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-amber-700 dark:text-amber-400 text-sm">
                    Aucun planning enregistré pour la semaine du {mondayStr} — personne ne peut être assigné tant que le planning de cette semaine n'est pas complété dans le module Planning.
                </div>
            )}
            {!loading && planningWeek !== null && !modifiedToday && lastModifiedDate && (
                <div className="mb-4 px-4 py-2.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-blue-700 dark:text-blue-400 text-sm">
                    Cette feuille de route n'a pas encore été modifiée aujourd'hui — elle reprend les affectations du {format(parseISO(lastModifiedDate), 'EEEE d MMMM', { locale: fr })}.
                </div>
            )}

            {loading ? (
                <p className="text-slate-400 text-sm">Chargement...</p>
            ) : (
                <div className="flex items-start gap-4">
                <div className="relative overflow-x-auto rounded-2xl bg-slate-100/50 dark:bg-black/10 p-4">
                    <div className="relative bg-white dark:bg-[var(--bg-card)]" style={{ width: pageWidthPx, minHeight: canvasHeight }}>
                        {/* Repères de pages imprimées (pointillés) */}
                        {Array.from({ length: pageCount }).map((_, i) => (
                            <div key={i} className="absolute border border-dashed border-slate-300 dark:border-slate-600 pointer-events-none" style={{ left: 0, top: i * pageHeightPx, width: pageWidthPx, height: pageHeightPx }}>
                                <div className="absolute border border-dashed border-brand-300 dark:border-brand-500/40 pointer-events-none" style={{ left: marginLeftPx, top: marginTopPx, width: pageWidthPx - marginLeftPx - marginRightPx, height: pageHeightPx - marginTopPx - marginBottomPx }} />
                                <span className="absolute -top-5 left-0 text-[10px] font-bold text-slate-400">Page {i + 1}</span>
                            </div>
                        ))}

                        {/* Titre + établissement/service/date : texte simple, sans encadrement coloré —
                            demande d'Emeni ("retire l'encadré bleu dans le titre"). Position et largeur
                            fixes (toujours en haut, pleine largeur imprimable), plus déplaçable du tout —
                            pour ne plus jamais se retrouver sous un tableau. Titre et sous-titre restent
                            renommables/redimensionnables, juste plus glissables. Centré, comme dans le PDF. */}
                        <div
                            ref={setNodeRef('title:title')}
                            className="absolute group"
                            style={{ left: marginLeftPx, top: marginTopPx, width: pageWidthPx - marginLeftPx - marginRightPx }}
                        >
                            <div className="py-2 text-center">
                                {editingTitleLabel ? (
                                    <input
                                        autoFocus
                                        value={titleLabelDraft}
                                        onChange={(e) => setTitleLabelDraft(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') saveTitleLabel(); if (e.key === 'Escape') { setEditingTitleLabel(false); setTitleLabelDraft(titleLayout.label); } }}
                                        onBlur={saveTitleLabel}
                                        className="w-full max-w-xs mx-auto block text-center font-black bg-slate-50 dark:bg-[var(--bg-soft)] text-slate-800 dark:text-content rounded px-2 py-0.5 outline-none border border-brand-400"
                                        style={{ fontSize: titleLayout.fontSize }}
                                    />
                                ) : (
                                    <h1
                                        className="font-black break-words cursor-text hover:underline decoration-dotted inline-block text-slate-800 dark:text-content"
                                        style={{ fontSize: titleLayout.fontSize }}
                                        onClick={() => setEditingTitleLabel(true)}
                                        title="Cliquer pour renommer"
                                    >
                                        {titleLayout.label}
                                    </h1>
                                )}
                                <p className="font-bold mt-0.5 text-slate-500 dark:text-slate-400" style={{ fontSize: metaLayout.fontSize }}>
                                    {user?.company_name || 'Votre Établissement'} — {activeBoard?.sheetMode === 'split' ? `${activeBoard.name} — ${activeService === 'midi' ? 'Midi' : 'Soir'}` : (activeBoard?.name || 'Feuille de route')} — {format(date, 'EEEE d MMMM yyyy', { locale: fr })}
                                </p>
                            </div>
                            <div className="absolute top-1.5 right-3 flex items-center gap-3 opacity-0 group-hover:opacity-100">
                                <div className="flex items-center gap-1">
                                    <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Titre</span>
                                    <FontStepper size={titleLayout.fontSize} onChange={changeTitleFontSize} />
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Sous-titre</span>
                                    <FontStepper size={metaLayout.fontSize} onChange={changeMetaFontSize} />
                                </div>
                            </div>
                        </div>

                        {activeTables.length === 0 && activeTextBoxes.length === 0 && (
                            <div className="absolute text-center text-slate-400" style={{ left: 20, top: 120 }}>
                                <p className="mb-3">Aucun tableau pour la feuille {activeBoard?.sheetMode === 'split' ? (activeService === 'midi' ? 'Midi' : 'Soir') : (activeBoard?.name || '')}.</p>
                                <button onClick={() => setAddTableOpen(true)} className="text-brand-500 font-bold underline">Créer le premier tableau</button>
                            </div>
                        )}

                        {activeTables.map(table => (
                            <div
                                key={table.id}
                                ref={setNodeRef(`table:${table.id}`)}
                                onMouseDown={() => setSelectedElement({ kind: 'table', id: table.id })}
                                className={`absolute bg-white dark:bg-[var(--bg-card)] border rounded-2xl shadow-sm ${selectedElement?.kind === 'table' && selectedElement.id === table.id ? 'border-brand-400 ring-2 ring-brand-200 dark:ring-brand-500/30' : 'border-slate-200 dark:border-border'}`}
                                style={{ left: table.posX, top: table.posY, width: table.width, minHeight: table.minHeight || undefined }}
                            >
                                <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-100 dark:border-border cursor-move" onMouseDown={(e) => startDrag(e, 'table', table.id)}>
                                    <GripVertical size={16} className="text-slate-300 shrink-0" />
                                    {editingTableId === table.id ? (
                                        <input
                                            autoFocus
                                            value={tableTitleDraft}
                                            onChange={(e) => setTableTitleDraft(e.target.value)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => { if (e.key === 'Enter') saveTableTitle(table.id); if (e.key === 'Escape') setEditingTableId(null); }}
                                            className="flex-1 min-w-0 bg-slate-50 dark:bg-[var(--bg-soft)] border border-brand-400 rounded px-1.5 py-0.5 outline-none"
                                            style={{ fontSize: table.titleFontSize }}
                                        />
                                    ) : (
                                        <span className="flex-1 min-w-0 font-black text-slate-800 dark:text-content break-words" style={{ fontSize: table.titleFontSize }}>{table.title}</span>
                                    )}
                                    <div className="flex items-center shrink-0" onMouseDown={(e) => e.stopPropagation()}>
                                        <FontStepper size={table.titleFontSize} onChange={(d) => changeTableTitleFontSize(table, d)} />
                                        {editingTableId === table.id ? (
                                            <button onClick={() => saveTableTitle(table.id)} className="p-1 text-emerald-600"><Check size={14} /></button>
                                        ) : (
                                            <button onClick={() => { setEditingTableId(table.id); setTableTitleDraft(table.title); }} className="p-1 text-slate-400 hover:text-brand-500"><Pencil size={14} /></button>
                                        )}
                                        <button onClick={() => duplicateTable(table.id)} className="p-1 text-slate-400 hover:text-brand-500" title="Dupliquer ce tableau"><Copy size={14} /></button>
                                        <button onClick={() => deleteTable(table.id)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                                    </div>
                                </div>

                                <div ref={setRowBodyRef(table.id)} className="relative p-2 space-y-1" onDragOver={(e) => handleRowDragOverTableBody(e, table.id)} onDrop={handleRowDrop}>
                                    {/* Relie visuellement les rangs assignés au même salarié, sans les déplacer —
                                        demande d'Emeni. À droite (pas au milieu du texte des rangs), avec un
                                        couloir par salarié pour que deux lignes qui se chevauchent en hauteur ne
                                        se croisent jamais (autre demande d'Emeni, remonté avec capture à
                                        l'appui). Mesuré sur le DOM (rangs de hauteurs variables). */}
                                    {(connectorsByTable[table.id] || []).length > 0 && (
                                        <svg
                                            className="absolute right-0 top-0 pointer-events-none z-10"
                                            width={CONNECTOR_GUTTER_PAD + (connectorLanesByTable[table.id] || 1) * CONNECTOR_LANE_WIDTH}
                                            height="100%"
                                            style={{ overflow: 'visible' }}
                                        >
                                            {connectorsByTable[table.id].map((g, i) => {
                                                const x = CONNECTOR_GUTTER_PAD + (connectorLanesByTable[table.id] - 1 - g.lane) * CONNECTOR_LANE_WIDTH + CONNECTOR_LANE_WIDTH / 2;
                                                return (
                                                    <g key={i}>
                                                        <line x1={x} y1={g.top} x2={x} y2={g.bottom} stroke={g.color} strokeWidth={2} strokeOpacity={0.55} />
                                                        {g.dots.map((y, j) => <circle key={j} cx={x} cy={y} r={2.5} fill={g.color} />)}
                                                    </g>
                                                );
                                            })}
                                        </svg>
                                    )}
                                    <div className="flex items-center gap-2 px-2 pb-1.5 mb-0.5 border-b border-slate-100 dark:border-border">
                                        {table.columns.map(col => editingColumnId === col.id ? (
                                            <input
                                                key={col.id}
                                                autoFocus
                                                value={headerDraft}
                                                onChange={(e) => setHeaderDraft(e.target.value)}
                                                onBlur={() => saveColumnHeader(table.id, col.id)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') saveColumnHeader(table.id, col.id); if (e.key === 'Escape') setEditingColumnId(null); }}
                                                className={`text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest bg-slate-50 dark:bg-[var(--bg-soft)] border border-brand-400 rounded px-1 py-0.5 outline-none ${col.type === 'text' ? 'shrink-0 max-w-[35%]' : 'flex-1 min-w-0'}`}
                                            />
                                        ) : (
                                            <span
                                                key={col.id}
                                                onClick={() => startEditHeader(col.id, col.header)}
                                                className={`text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest break-words cursor-pointer hover:underline decoration-dotted ${col.type === 'text' ? 'shrink-0 max-w-[35%]' : 'flex-1 min-w-0'}`}
                                                title="Cliquer pour renommer la colonne"
                                            >
                                                {col.header}
                                            </span>
                                        ))}
                                    </div>
                                    {table.rows.map(row => (
                                        <div
                                            key={row.id}
                                            ref={setRowNodeRef(row.id)}
                                            draggable
                                            onDragStart={(e) => handleRowDragStart(e, row.id)}
                                            onDragOver={(e) => handleRowDragOverRow(e, table.id, row.id)}
                                            onDrop={handleRowDrop}
                                            onDragEnd={handleRowDragEnd}
                                            className={`relative flex items-center gap-2 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-surface-soft group transition-opacity ${draggingRowId === row.id ? 'opacity-30' : 'opacity-100'}`}
                                            style={{ paddingTop: rowPaddingPx(row.fontSize), paddingBottom: rowPaddingPx(row.fontSize) }}
                                        >
                                            <GripVertical size={14} className="text-slate-300 opacity-0 group-hover:opacity-100 shrink-0 cursor-grab" />
                                            {table.columns.map(col => {
                                                if (col.type === 'assign') {
                                                    const assignedIds = assignments[row.id]?.[col.id] || [];
                                                    const sortedList = sortedEligibleFor(row.id, col.id);
                                                    const notEligibleAssignedIds = assignedIds.filter(id => !sortedList.some(e => e.id === id));
                                                    const key = `${row.id}:${col.id}`;
                                                    return (
                                                        <AssignDropdown
                                                            key={col.id}
                                                            rowId={row.id}
                                                            assignedIds={assignedIds}
                                                            sortedList={sortedList}
                                                            notEligibleAssignedIds={notEligibleAssignedIds}
                                                            employeeNameById={employeeNameById}
                                                            employeeFirstNameById={employeeFirstNameById}
                                                            employeePosteById={employeePosteById}
                                                            rangTier={(rid, eid) => rangTier(rid, col.id, eid)}
                                                            tierColor={tierColors}
                                                            isOpen={openAssignKey === key}
                                                            fontSize={row.fontSize}
                                                            onToggle={() => setOpenAssignKey(v => v === key ? null : key)}
                                                            onClose={() => setOpenAssignKey(null)}
                                                            onToggleAssign={(empId) => toggleAssign(row.id, col.id, empId)}
                                                            onClear={() => clearAssign(row.id, col.id)}
                                                        />
                                                    );
                                                }
                                                const value = row.cells[col.id] || '';
                                                const isEditing = editingCell?.rowId === row.id && editingCell.columnId === col.id;
                                                return isEditing ? (
                                                    <input
                                                        key={col.id}
                                                        autoFocus
                                                        value={editCellDraft}
                                                        onChange={(e) => setEditCellDraft(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') saveCellValue(row.id, col.id); if (e.key === 'Escape') setEditingCell(null); }}
                                                        onBlur={() => saveCellValue(row.id, col.id)}
                                                        style={{ fontSize: row.fontSize }}
                                                        className="font-semibold text-slate-700 dark:text-content shrink-0 max-w-[35%] min-w-0 bg-slate-50 dark:bg-[var(--bg-soft)] border border-brand-400 rounded px-1 outline-none"
                                                    />
                                                ) : (
                                                    <span
                                                        key={col.id}
                                                        onClick={() => startEditCell(row.id, col.id, value)}
                                                        title="Cliquer pour modifier"
                                                        className="font-semibold text-slate-600 dark:text-slate-300 shrink-0 break-words max-w-[35%] cursor-text hover:underline decoration-dotted"
                                                        style={{ fontSize: row.fontSize }}
                                                    >
                                                        {value || <span className="text-slate-300 dark:text-slate-600 italic">—</span>}
                                                    </span>
                                                );
                                            })}
                                            <div className="opacity-0 group-hover:opacity-100 shrink-0">
                                                <FontStepper size={row.fontSize} onChange={(d) => changeRowFontSize(row, d)} />
                                            </div>
                                            <button onClick={() => deleteRow(row.id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-red-500 shrink-0"><Trash2 size={13} /></button>
                                            {/* Réserve la place du couloir de repères à droite (toujours, pas
                                                seulement au survol) pour que le contenu du rang ne passe jamais
                                                sous les lignes de connexion. */}
                                            {(connectorLanesByTable[table.id] || 0) > 0 && (
                                                <div className="shrink-0" style={{ width: CONNECTOR_GUTTER_PAD + connectorLanesByTable[table.id] * CONNECTOR_LANE_WIDTH }} />
                                            )}
                                        </div>
                                    ))}

                                    {addRowForTable === table.id ? (
                                        <div className="flex items-center gap-1 px-2 py-1">
                                            {table.columns.some(c => c.type === 'text') && (
                                                <input
                                                    autoFocus
                                                    value={rowLabelDraft}
                                                    onChange={(e) => setRowLabelDraft(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') submitNewRow(table.id); if (e.key === 'Escape') setAddRowForTable(null); }}
                                                    placeholder="Nom du rang"
                                                    className="flex-1 min-w-0 text-sm bg-slate-50 dark:bg-[var(--bg-soft)] border border-brand-400 rounded-lg px-2 py-1 outline-none"
                                                />
                                            )}
                                            <button onClick={() => submitNewRow(table.id)} className="p-1 text-emerald-600"><Check size={16} /></button>
                                            <button onClick={() => setAddRowForTable(null)} className="p-1 text-slate-400"><X size={16} /></button>
                                        </div>
                                    ) : (
                                        <button onClick={() => { setAddRowForTable(table.id); setRowLabelDraft(''); }} className="flex items-center gap-1 text-xs font-bold text-brand-500 px-2 py-1.5 hover:underline">
                                            <Plus size={13} /> Ajouter une ligne
                                        </button>
                                    )}
                                </div>

                                <div
                                    onMouseDown={(e) => startResize(e, 'table', table.id)}
                                    className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize opacity-40 hover:opacity-100"
                                    title="Redimensionner"
                                >
                                    <svg viewBox="0 0 16 16" className="w-full h-full text-slate-400"><path d="M14 14L14 2M14 14L2 14" stroke="currentColor" strokeWidth="2" fill="none" /></svg>
                                </div>
                            </div>
                        ))}

                        {activeTextBoxes.map(box => (
                            <div
                                key={box.id}
                                ref={setNodeRef(`textbox:${box.id}`)}
                                onMouseDown={() => setSelectedElement({ kind: 'textbox', id: box.id })}
                                className={`absolute rounded-xl group bg-yellow-50/90 dark:bg-[var(--bg-soft)] border border-dashed shadow-sm ${selectedElement?.kind === 'textbox' && selectedElement.id === box.id ? 'border-brand-400 ring-2 ring-brand-200 dark:ring-brand-500/30' : 'border-slate-300 dark:border-border'}`}
                                style={{ left: box.posX, top: box.posY, width: box.width, height: box.height }}
                            >
                                <div className="flex items-center px-1.5 py-1 opacity-0 group-hover:opacity-100" onMouseDown={(e) => startDrag(e, 'textbox', box.id)}>
                                    <GripVertical size={14} className="text-slate-300 shrink-0 cursor-move" />
                                    <div className="flex items-center gap-0.5 shrink-0 ml-1" onMouseDown={(e) => e.stopPropagation()}>
                                        <button onClick={() => toggleTextBoxBold(box)} className={`p-1 rounded ${box.bold ? 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-content' : 'text-slate-400 hover:text-slate-600'}`}><Bold size={12} /></button>
                                        <button onClick={() => toggleTextBoxItalic(box)} className={`p-1 rounded ${box.italic ? 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-content' : 'text-slate-400 hover:text-slate-600'}`}><Italic size={12} /></button>
                                        <button onClick={() => setTextBoxAlign(box, 'left')} className={`p-1 rounded ${box.align === 'left' ? 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-content' : 'text-slate-400 hover:text-slate-600'}`}><AlignLeft size={12} /></button>
                                        <button onClick={() => setTextBoxAlign(box, 'center')} className={`p-1 rounded ${box.align === 'center' ? 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-content' : 'text-slate-400 hover:text-slate-600'}`}><AlignCenter size={12} /></button>
                                        <button onClick={() => setTextBoxAlign(box, 'right')} className={`p-1 rounded ${box.align === 'right' ? 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-content' : 'text-slate-400 hover:text-slate-600'}`}><AlignRight size={12} /></button>
                                    </div>
                                    <div className="flex-1" />
                                    <div className="flex items-center shrink-0" onMouseDown={(e) => e.stopPropagation()}>
                                        <FontStepper size={box.fontSize} onChange={(d) => changeTextBoxFontSize(box, d)} />
                                        <button onClick={() => duplicateTextBox(box.id)} className="p-1 text-slate-400 hover:text-brand-500" title="Dupliquer cette zone de texte"><Copy size={13} /></button>
                                        <button onClick={() => deleteTextBox(box.id)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
                                    </div>
                                </div>
                                <textarea
                                    value={box.content}
                                    onChange={(e) => setTextBoxes(prev => prev.map(t => t.id === box.id ? { ...t, content: e.target.value } : t))}
                                    onBlur={(e) => saveTextBoxContent(box.id, e.target.value)}
                                    style={{ fontSize: box.fontSize, height: box.height - 30, fontWeight: box.bold ? 700 : 400, fontStyle: box.italic ? 'italic' : 'normal', textAlign: box.align }}
                                    className="w-full resize-none bg-transparent outline-none px-2 pb-2 text-slate-700 dark:text-content"
                                />
                                <div
                                    onMouseDown={(e) => startResize(e, 'textbox', box.id)}
                                    className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize opacity-0 group-hover:opacity-60"
                                    title="Redimensionner"
                                >
                                    <svg viewBox="0 0 16 16" className="w-full h-full text-slate-400"><path d="M14 14L14 2M14 14L2 14" stroke="currentColor" strokeWidth="2" fill="none" /></svg>
                                </div>
                            </div>
                        ))}

                        {/* Repères d'alignement (rendus au-dessus de tout pendant le glisser) */}
                        {guides.map((g, i) => g.type === 'v' ? (
                            <div key={i} className="absolute top-0 pointer-events-none z-30" style={{ left: g.pos, height: canvasHeight, borderLeft: `1px dashed ${GUIDE_COLOR}` }} />
                        ) : (
                            <div key={i} className="absolute left-0 pointer-events-none z-30" style={{ top: g.pos, width: pageWidthPx, borderTop: `1px dashed ${GUIDE_COLOR}` }} />
                        ))}
                    </div>
                </div>

                {showDayPreview && (
                    <div className="hidden lg:block flex-1 min-w-[20rem] max-w-3xl shrink-0 sticky top-4 bg-white dark:bg-[var(--bg-card)] border border-slate-200 dark:border-border rounded-2xl shadow-sm p-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
                        <div className="flex items-center gap-2 mb-3">
                            <Clock size={16} className="text-brand-500 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Aperçu planning</p>
                                <p className="text-sm font-bold text-slate-700 dark:text-content capitalize truncate">{format(date, 'EEEE d MMMM', { locale: fr })}</p>
                            </div>
                            <button
                                onClick={() => setDayPreviewOnServiceOnly(v => !v)}
                                className={`shrink-0 p-1.5 rounded-lg border transition-all ${dayPreviewOnServiceOnly ? 'bg-brand-50 dark:bg-brand-500/10 border-brand-300 dark:border-brand-500/40 text-brand-600' : 'border-slate-200 dark:border-border text-slate-400 hover:text-slate-600 dark:hover:text-content'}`}
                                title="N'afficher que ceux sur le service (masquer repos/absences)"
                            >
                                <UserCheck size={14} />
                            </button>
                            {jobPosts.length > 0 && (
                                <div className="shrink-0">
                                    <button
                                        ref={dayPreviewFilterBtnRef}
                                        onClick={() => setDayPreviewFilterOpen(v => !v)}
                                        className={`p-1.5 rounded-lg border transition-all ${dayPreviewRoleFilter.length > 0 ? 'bg-brand-50 dark:bg-brand-500/10 border-brand-300 dark:border-brand-500/40 text-brand-600' : 'border-slate-200 dark:border-border text-slate-400 hover:text-slate-600 dark:hover:text-content'}`}
                                        title="Filtrer par poste"
                                    >
                                        <Filter size={14} />
                                    </button>
                                    {dayPreviewFilterOpen && dayPreviewFilterPos && createPortal(
                                        <>
                                            <div className="fixed inset-0 z-[100]" onClick={() => setDayPreviewFilterOpen(false)} />
                                            <div
                                                className="fixed w-56 max-h-72 overflow-y-auto bg-white dark:bg-[var(--bg-card)] border border-slate-200 dark:border-border rounded-xl shadow-xl z-[101]"
                                                style={{ top: dayPreviewFilterPos.top, left: dayPreviewFilterPos.left }}
                                            >
                                                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-border">
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Postes affichés</span>
                                                    {dayPreviewRoleFilter.length > 0 && (
                                                        <button onClick={() => setDayPreviewRoleFilter([])} className="text-[10px] font-bold text-brand-500 hover:underline">Tout afficher</button>
                                                    )}
                                                </div>
                                                {jobPosts.map(p => {
                                                    const pid = String(p.id);
                                                    const checked = dayPreviewRoleFilter.includes(pid);
                                                    return (
                                                        <button
                                                            key={pid}
                                                            onClick={() => setDayPreviewRoleFilter(prev => checked ? prev.filter(id => id !== pid) : [...prev, pid])}
                                                            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-surface-soft"
                                                        >
                                                            <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-brand-600 border-brand-600' : 'border-slate-300 dark:border-border'}`}>
                                                                {checked && <Check size={11} className="text-brand-fg" />}
                                                            </span>
                                                            <span className="text-slate-700 dark:text-content truncate">{p.title}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </>,
                                        document.body
                                    )}
                                </div>
                            )}
                        </div>
                        {dayExtras.length > 0 && (
                            <div className="mb-4 space-y-1.5">
                                <p className="text-[10px] font-black uppercase tracking-widest text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-500/10 rounded px-2 py-1">Extras</p>
                                {dayExtras.map(extra => (
                                    <div key={extra.id} className="bg-[#f5f3ff] dark:bg-purple-500/10 rounded-lg p-2 border border-purple-100 dark:border-purple-500/20">
                                        <p className="text-[11px] font-black text-purple-900 dark:text-purple-300 uppercase leading-tight truncate">{extra.count}x {extra.label}</p>
                                        <p className="text-[10px] text-purple-500 dark:text-purple-400 font-bold mt-0.5">{extra.start}-{extra.end}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                        {dayPreviewGroups.length === 0 && dayExtras.length === 0 ? (
                            <p className="text-xs text-slate-400">Personne de prévu ce jour dans le planning.</p>
                        ) : (
                            <div className="space-y-4">
                                {dayPreviewGroups.map(group => (
                                    <div key={group.role}>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/10 rounded px-2 py-1 mb-1.5">
                                            {group.role} <span className="opacity-60 font-normal">({group.people.length})</span>
                                        </p>
                                        <ul className="space-y-2">
                                            {group.people.map(p => (
                                                <li key={p.employeeId} className="text-sm border-b border-slate-100 dark:border-border pb-2 last:border-0 last:pb-0">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="flex items-center gap-1.5 min-w-0">
                                                            <span className="font-bold text-slate-700 dark:text-content break-words">{p.name}</span>
                                                            {p.isExtra && <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-amber-100 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400">Extra</span>}
                                                        </div>
                                                        {p.durationLabel && <span className="shrink-0 text-[10px] text-slate-400 dark:text-slate-500 font-semibold flex items-center gap-1"><Clock size={10} /> {p.durationLabel}</span>}
                                                    </div>
                                                    {/* Mêmes créneaux/couleurs que le vrai module Planning (composant ShiftCell partagé) — demande d'Emeni.
                                                        Cliquable pour modifier directement depuis l'aperçu, comme sur le planning de base. */}
                                                    <div
                                                        className="h-8 mt-1 max-w-xs cursor-pointer"
                                                        onClick={(e) => {
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            setDayPreviewEditorPos({ x: rect.left, y: rect.bottom + window.scrollY });
                                                            setEditingDayPreviewShift({ shift: p.shift, employeeId: p.employeeId, employeeName: p.name, employeeRole: p.roleId, date: dateStr });
                                                        }}
                                                    >
                                                        <ShiftCell shift={p.shift} templates={templates} absenceTypes={absenceTypes} isDayView />
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                        {editingDayPreviewShift && createPortal(
                            // Rendu par-dessus toute la page (portail) plutôt qu'à l'intérieur de ce panneau
                            // sticky/overflow-y-auto : sans ça, le `position: absolute` de ShiftEditor se
                            // calcule par rapport au panneau (qui établit son propre contexte de positionnement)
                            // au lieu du viewport, et se retrouve coupé par le défilement — même bug déjà vu et
                            // corrigé sur le filtre de postes de ce même panneau.
                            <ShiftEditor
                                shift={editingDayPreviewShift.shift}
                                employeeName={editingDayPreviewShift.employeeName}
                                employeeRoleId={editingDayPreviewShift.employeeRole}
                                availableTemplates={templates}
                                onSave={handleDayPreviewShiftUpdate}
                                onClose={() => setEditingDayPreviewShift(null)}
                                position={dayPreviewEditorPos}
                                currentDate={editingDayPreviewShift.date}
                                absenceTypes={absenceTypes}
                            />,
                            document.body
                        )}
                    </div>
                )}
                </div>
            )}

            {addTableOpen && activeSheetId && (
                <AddRoadmapTableModal
                    sheetId={activeSheetId}
                    onClose={() => setAddTableOpen(false)}
                    onCreated={handleTableCreated}
                />
            )}
        </div>
    );
};
